import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
    ArrowLeft, UserPlus, X, Edit, 
    Clock, Factory, Sun, Moon, Layers, IndianRupee, 
    CheckCircle, Calendar, CalendarDays, Activity, ChevronDown, ChevronUp, ChevronRight, 
    ArrowRightLeft, AlertTriangle, Search, FileText, Download, Filter,
    Camera, User, CheckSquare, Square, Zap, ZapOff, Trash2, MoreVertical, UserX,
    ArrowRight, Grid, EyeOff, Bookmark, Timer, Link as LinkIcon, Info, UserMinus,
   Volume2, VolumeX, HelpCircle, PlayCircle, Menu
} from 'lucide-react';

import BadliModal from './BadliModal';

import WorkerHistory from './WorkerHistory'; 
import { useSmartGuide } from './useSmartGuide'; 


// --- IMPORT NEW GLOBAL UI COMPONENTS ---
import Card from '../../ui/Card';
import Modal from '../../ui/Modal';
import Button from '../../ui/Button';
import Badge from '../../ui/Badge';
import Input from '../../ui/Input';
import Tabs from '../../ui/Tabs';
import IconBox from '../../ui/IconBox';
import ConfirmModal from '../../ui/ConfirmModal';
import EditWorkerModal from '../../ui/EditWorkerModal';
import SmartGuideModal from '../../ui/SmartGuideModal';

const API_BASE = 'https://mukadampro.shirpur.store';



// --- UNIVERSAL TIME & DATE PARSERS ---
const parseTimeStr = (t) => { 
    if (!t) return 0;
    let timeStr = String(t).trim();
    timeStr = timeStr.replace(/^\d{4}-\d{2}-\d{2}\s+/, '');
    
    let isPM = timeStr.toUpperCase().includes('PM');
    let isAM = timeStr.toUpperCase().includes('AM');
    
    let cleanTime = timeStr.replace(/PM|AM/i, '').trim();
    let [h, m] = cleanTime.split(':').map(Number);
    
    h = h || 0;
    m = m || 0;
    
    if (isPM && h !== 12) h += 12;
    if (isAM && h === 12) h = 0;
    
    return h * 60 + m; 
};

const formatTimeForDisplay = (t) => {
    if (!t) return "";
    let timeStr = String(t).trim();
    timeStr = timeStr.replace(/^\d{4}-\d{2}-\d{2}\s+/, ''); 
    if (timeStr.toUpperCase().includes('PM') || timeStr.toUpperCase().includes('AM')) return timeStr;
    let [h, m] = timeStr.split(':');
    let hr = parseInt(h, 10) || 0;
    const ampm = hr >= 12 ? 'PM' : 'AM';
    hr = hr % 12 || 12;
    return `${hr}:${m} ${ampm}`;
};

// --- HINDI VOICE TIME & DURATION HELPERS ---
const formatHindiVoiceTime = (timeStr) => {
    if (!timeStr) return "";
    let cleanStr = String(timeStr).trim().replace(/^\d{4}-\d{2}-\d{2}\s+/, '');
    let isPM = cleanStr.toUpperCase().includes('PM');
    let isAM = cleanStr.toUpperCase().includes('AM');
    
    let timePart = cleanStr.replace(/PM|AM/i, '').trim();
    let [h, m] = timePart.split(':').map(Number);
    
    if (isNaN(h)) return "";
    m = m || 0;
    
    let hour24 = h;
    if (isPM && h !== 12) hour24 += 12;
    if (isAM && h === 12) hour24 = 0;
    
    let timeWord = "";
    // Raat: 8 PM (20) to 4 AM (4)
    if (hour24 >= 20 || hour24 <= 4) {
        timeWord = " रात";
    } 
    // Subah: 5 AM (5) to 11 AM (11)
    else if (hour24 >= 5 && hour24 <= 11) {
        timeWord = " सुबह";
    } 
    // Dopaher: 12 PM (12) to 4 PM (16)
    else if (hour24 >= 12 && hour24 <= 16) {
        timeWord = " दोपहर";
    } 
    // Shaam: 5 PM (17) to 7 PM (19)
    else if (hour24 >= 17 && hour24 <= 19) {
        timeWord = " शाम";
    }
    
    let displayHour = h % 12 || 12;
    let timeText = m > 0 ? `${displayHour} बजकर ${m} मिनट` : `${displayHour} बजे`;
    return `${timeWord} ${timeText}`;
};

const getDurationHindiText = (startStr, endStr) => {
    const startMins = parseTimeStr(startStr); 
    const endMins = parseTimeStr(endStr);
    if(startMins === undefined || endMins === undefined) return "";
    
    let diff = endMins - startMins;
    if (diff < 0) diff += 1440; 
    
    const h = Math.floor(diff / 60);
    const m = diff % 60;
    
    let text = [];
    if (h > 0) text.push(`${h} घंटे`);
    if (m > 0) text.push(`${m} मिनट`);
    return text.join(" ");
};

// --- UTILS ---
const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency", currency: "INR", minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(amount || 0).replace("₹", "₹ ");
};

const getLoomNumber = (id) => {
    if (!id) return 0;
    const str = String(id);
    const digits = str.replace(/[^0-9]/g, '');
    return digits ? parseInt(digits, 10) : 0;
};

const formatSimpleLoomList = (loomStr) => {
    if (!loomStr) return "";
    const nums = [...new Set(loomStr.toString().split(',')
        .map(s => parseInt(s.replace(/[^0-9]/g, ''), 10))
        .filter(n => !isNaN(n))
        .sort((a, b) => a - b))];
    return nums.join(', ');
};

const getLoomArray = (loomStr) => {
    if (!loomStr) return [];
    return loomStr.toString().split(',')
        .map(s => s.replace(/[^0-9]/g, '').trim())
        .filter(s => s !== '');
};

// --- HELPER: CHECK IF LOOM IS ACTIVE ---
const isLoomActiveHelper = (lastUpdated) => {
    if (!lastUpdated) return false;
    const updated = new Date(lastUpdated.replace(' ', 'T'));
    const now = new Date();
    const diffMinutes = (now - updated) / 1000 / 60;
    return !isNaN(diffMinutes) && diffMinutes < 10;
};

const formatFullDateTime = (timestamp) => {
    if (!timestamp) return "Loading...";
    try {
        const d = new Date(timestamp);
        return d.toLocaleTimeString('en-US', { hour12: true, hour: '2-digit', minute: '2-digit', second: '2-digit' }) + 
               " | " + String(d.getDate()).padStart(2, '0') + "-" + String(d.getMonth() + 1).padStart(2, '0') + "-" + d.getFullYear() +
               " | " + d.toLocaleDateString('en-US', { weekday: 'long' });
    } catch (error) { return "Time Unavailable"; }
};

// --- CENTRALIZED ACTIVE CHECK LOGIC ---
const isWorkerOccupyingLooms = (worker, currentTime) => {
    if (worker.on_duty == 1) return true;
    if (worker.overtime == 1 && worker.on_duty == 1) return true;

    if (worker.is_badli_active == 1) {
        if (!worker.badli_start || !worker.badli_end) return true; 
        
        const now = new Date(currentTime);
        const currentMins = now.getHours() * 60 + now.getMinutes();

        const start = parseTimeStr(worker.badli_start); 
        const end = parseTimeStr(worker.badli_end);
        
        if (worker.badli_from_date && worker.badli_to_date) {
            let logicalDate = new Date(currentTime);
            if (end < start && currentMins < end) {
                logicalDate.setDate(logicalDate.getDate() - 1);
            }
            
            const year = logicalDate.getFullYear();
            const month = String(logicalDate.getMonth() + 1).padStart(2, '0');
            const day = String(logicalDate.getDate()).padStart(2, '0');
            const logicalTodayStr = `${year}-${month}-${day}`;

            const normalizeDate = (dStr) => {
                const parts = String(dStr).split(' ')[0].trim().split('-');
                if (parts.length === 3 && parts[0].length === 2) { 
                    return `${parts[2]}-${parts[1]}-${parts[0]}`; 
                }
                return String(dStr).split(' ')[0].trim();
            };

            const fromDateStr = normalizeDate(worker.badli_from_date);
            const toDateStr = normalizeDate(worker.badli_to_date);

            if (logicalTodayStr < fromDateStr || logicalTodayStr > toDateStr) return false; 
        }
        
        if (end > start) { 
            return currentMins >= start && currentMins < end; 
        } else { 
            return currentMins >= start || currentMins < end; 
        }
    }
    return false;
};

// --- BADLI END MODAL (1000% PREMIUM SOLID WHITE UI - EXACTLY MATCHING OVERTIME) ---
const BadliEndModal = ({ onClose, onConfirm, worker, looms, handleVoiceClick }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // 🌟 Live Timer State
    const [liveTimer, setLiveTimer] = useState("00:00:00");

    // 🌟 Live Timer Effect (Har second update hone ke liye)
    useEffect(() => {
        if (!worker.badli_start) return;
        const timer = setInterval(() => {
            const now = new Date();
            const nowSecsTotal = (now.getHours() * 3600) + (now.getMinutes() * 60) + now.getSeconds();
            
            let startMins = parseTimeStr(worker.badli_start);
            let startSecs = startMins * 60;
            
            let diffSecs = nowSecsTotal - startSecs;
            if (diffSecs < 0) diffSecs += 86400; 
            
            const h = Math.floor(diffSecs / 3600);
            const m = Math.floor((diffSecs % 3600) / 60);
            const s = diffSecs % 60;
            
            setLiveTimer(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`);
        }, 1000);
        return () => clearInterval(timer);
    }, [worker.badli_start]);

    const workerLoomList = useMemo(() => getLoomArray(worker.looms), [worker]);
    const workerLoomData = useMemo(() => {
        if (!looms || !Array.isArray(looms)) return [];
        return looms.filter(l => workerLoomList.includes(String(l.loom_id)));
    }, [looms, workerLoomList]);

    const totalProduction = useMemo(() => {
        return workerLoomData.reduce((acc, curr) => acc + (parseFloat(curr.meters) || 0), 0);
    }, [workerLoomData]);

    const staticDuration = useMemo(() => {
        if (!worker.badli_start || !worker.badli_end) return "00:00 HOURS";
        try {
            let bStart = parseTimeStr(worker.badli_start);
            let bEnd = parseTimeStr(worker.badli_end);
            let bDiff = bEnd - bStart;
            if (bDiff < 0) bDiff += 1440;
            let bH = Math.floor(bDiff / 60);
            let bM = bDiff % 60;
            return bM > 0 ? `${bH}:${String(bM).padStart(2,'0')} HOURS` : `${bH} HOURS`;
        } catch (e) { return "00:00 HOURS"; }
    }, [worker.badli_start, worker.badli_end]);

    const handleConfirm = async () => {
        setIsSubmitting(true);
        await onConfirm(worker);
        setIsSubmitting(false);
        onClose();
    };

    // --- EXACT OVERTIME MODAL STYLES ---
    const modalStyles = {
        premiumCard: { background: '#FFFFFF', padding: '16px', borderRadius: '16px', border: '1px solid rgba(226, 232, 240, 0.8)', boxShadow: '0 4px 15px -3px rgba(15, 23, 42, 0.03)', marginBottom: '12px', position: 'relative' },
        label: { fontSize: '9.5px', fontWeight: '800', color: '#64748B', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px', textTransform:'uppercase', letterSpacing: '0.6px', justifyContent: 'center' },
        stickyBottom: { background: 'linear-gradient(145deg, #FFFFFF, #F8FAFC)', borderRadius: '16px', padding: '12px', boxShadow: '0 8px 25px -5px rgba(15, 23, 42, 0.05)', border: '1px solid rgba(226, 232, 240, 0.8)', marginTop: '16px', position: 'sticky', bottom: '0', zIndex: 10 },
        dangerSubmitBtn: { width: '100%', padding: '14px', background: 'linear-gradient(135deg, #DC2626, #EF4444)', color: '#FFFFFF', border: 'none', borderRadius: '12px', fontWeight: '900', cursor: 'pointer', fontSize:'11.5px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 6px 16px rgba(220, 38, 38, 0.25)', textTransform: 'uppercase', letterSpacing: '0.8px', transition: 'all 0.3s ease' }
    };

   return (
        <Modal 
            isOpen={true} 
            onClose={onClose} 
            title="END BADLI DUTY" 
            icon={UserMinus} 
            iconColor="#DC2626" 
            iconBg="#FEE2E2" 
            maxWidth="420px"
            bg="#FFFFFF" 
        >
            <style>{`
                ::-webkit-scrollbar { width: 4px; }
                ::-webkit-scrollbar-track { background: transparent; }
                ::-webkit-scrollbar-thumb { background: #E2E8F0; border-radius: 10px; }
            `}</style>
            
            <div style={{ marginTop: '0px', zoom: 0.95, width: '100%', boxSizing: 'border-box' }}>
                
                {/* 1. DURATION COMPACT STRIP (NAYA) */}     
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#FEF3C7', padding: '8px 12px', borderRadius: '10px', border: '1px solid #FDE68A', boxShadow: '0 2px 6px rgba(217, 119, 6, 0.08)' }}>
                        <Clock size={14} color="#D97706" />
                        <span style={{ fontSize: '11px', color: '#D97706', fontWeight: '800', letterSpacing: '0.3px', display: 'flex', alignItems: 'center', textTransform: 'uppercase' }}>
                            <span style={{ marginRight: '6px' }}>BADLI:</span>
                            {formatTimeForDisplay(worker.badli_start)}
                            <span style={{ fontSize: '9px', opacity: 0.6, margin: '0 6px', fontWeight: '700' }}>TO</span>
                            {formatTimeForDisplay(worker.badli_end)}
                            <span style={{ margin: '0 8px', color: '#FCD34D' }}>•</span>
                            {staticDuration}
                        </span>
                    </div>
                </div>
                
                {/* 2. TOTAL PRODUCTION CARD (UPDATE) */}
                <div style={{ ...modalStyles.premiumCard, textAlign: 'center' }}>
                    <label style={{...modalStyles.label, justifyContent: 'center', marginBottom: '6px'}}>
                        <Layers size={12} color="#64748B" /> TOTAL PRODUCTION
                    </label>
                    <div style={{ fontSize: '28px', fontWeight: '800', color: '#0F172A', marginTop: '4px' }}>
                        {totalProduction.toFixed(1)} <span style={{ fontSize: '13px', color: '#2563EB', fontWeight: '800' }}>METER</span>
                    </div>
                </div>

                {/* 3. LOOM WISE BREAKDOWN */}
                <div style={{ ...modalStyles.premiumCard }}>
                    <label style={{...modalStyles.label}}>
                        <Factory size={12} color="#64748B" /> LOOM WISE BREAKDOWN
                    </label>
                    <div style={{ maxHeight: '140px', overflowY: 'auto', marginTop: '12px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {workerLoomData.map(l => {
                                const isNightWorker = worker.shift === 'Night';
                                const globalLoom = looms && looms.find(gl => String(gl.loom_id || gl.id).replace(/[^0-9]/g, '') === String(l.loom_id));
                                const isActive = globalLoom ? isLoomActiveHelper(globalLoom.last_updated) : false;
                                const hasProd = parseFloat(l.meters) > 0;
                                
                                return (
                                    <div key={l.loom_id} style={{ 
                                        flex: '1 1 calc(33.333% - 8px)', background: '#FFFFFF', borderRadius: '12px', padding: '8px 4px', 
                                        boxShadow: '0 4px 10px rgba(15, 23, 42, 0.03)', border: '1px solid #E2E8F0', 
                                        display: 'flex', flexDirection: 'column', minWidth: '0', position: 'relative', overflow: 'hidden'
                                    }}>                        
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', padding: '0 4px' }}>
                                            <h3 style={{ fontSize: '9px', fontWeight: '900', color: '#94A3B8', margin: 0, letterSpacing: '0.2px' }}>LOOM {String(getLoomNumber(l.loom_id)).padStart(2, '0')}</h3>
                                            {isActive ? (
                                                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#10B981', boxShadow: '0 0 4px rgba(16, 185, 129, 0.8)' }}></span>
                                                </div>
                                            ) : (
                                                <span style={{ fontSize: '6px', fontWeight: '800', color: '#64748B', background: '#F1F5F9', padding: '2px 4px', borderRadius: '4px', letterSpacing: '0.5px' }}>OFF</span>
                                            )}
                                        </div>
                                        
                                        <div style={{ display: 'flex', width: '100%', flex: 1, alignItems: 'flex-end' }}>
                                            {hasProd ? (
                                                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '8px 2px', borderRadius: '8px', transition: 'all 0.3s ease', 
                                                    background: isNightWorker ? '#0F172A' : '#F8FAFC', 
                                                    border: isNightWorker ? '1px solid #1E293B' : '1px solid #E2E8F0',
                                                    boxShadow: isNightWorker ? '0 4px 10px rgba(15, 23, 42, 0.15)' : 'none'
                                                }}>
                                                    <div style={{ fontSize: '6px', fontWeight: '800', marginBottom: '4px', letterSpacing:'0.5px', color: isNightWorker ? '#94A3B8' : '#64748B' }}>{isNightWorker ? 'NIGHT SHIFT' : 'DAY SHIFT'}</div>
                                                    <div style={{ fontSize: '13px', fontWeight: '900', color: isNightWorker ? '#FFFFFF' : '#0F172A' }}>{parseFloat(l.meters).toFixed(1)}</div>
                                                </div>
                                            ) : (
                                                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '8px 2px', borderRadius: '8px', border: '1px dashed #CBD5E1', background: '#F8FAFC' }}>
                                                    <div style={{ fontSize: '7px', fontWeight: '900', color: '#94A3B8', opacity: 0.7, letterSpacing: '0.5px' }}>NO PROD</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            {workerLoomData.length === 0 && (
                                <span style={{fontSize:'10px', color:'#94A3B8', fontWeight:'700', textTransform: 'uppercase', width: '100%', textAlign: 'center', padding: '10px 0'}}>
                                    NO LOOMS ASSIGNED
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* STICKY BOTTOM ACTION AREA */}
                <div style={modalStyles.stickyBottom}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ background: '#FEE2E2', padding: '8px', borderRadius: '10px', border: '1px solid #FECACA' }}>
                                <Clock size={16} color="#DC2626" strokeWidth={2.5} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                                <span style={{ fontSize: '12px', fontWeight: '800', color: '#0F172A', letterSpacing: '-0.2px', textTransform: 'uppercase' }}>
                                    BADLI DUTY ACTIVE
                                </span>
                                <span style={{ fontSize: '9px', color: '#64748B', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                    LIVE DURATION
                                </span>
                            </div>
                        </div>
                        {/* 🌟 LIVE TIMER DISPLAY WIDGET */}
                        <div style={{ display: 'flex', alignItems: 'center', background: '#FEF2F2', padding: '4px 8px', borderRadius: '6px', border: '1px solid #FECACA' }}>
                            <span style={{ fontSize: '14px', fontWeight: '800', color: '#DC2626', letterSpacing: '1px', fontFamily: 'monospace' }}>
                                {liveTimer}
                            </span>
                        </div>
                    </div>
                    <button 
                        onClick={(e) => handleVoiceClick ? handleVoiceClick(e, " यह end Duty Button है, इससे बदली वर्कर की Duty हमेशा के लिए बंद हो जाएगी और सेव हो जाएगी।", handleConfirm, 'btn-badli-end-confirm') : handleConfirm(e)} 
                        disabled={isSubmitting} 
                        style={modalStyles.dangerSubmitBtn}
                    >
                        <CheckCircle size={16} color="#FFFFFF" strokeWidth={2.5} />
                        <span>{isSubmitting ? 'SAVING...' : 'END BADLI DUTY & SAVE'}</span>
                    </button>
                </div>

            </div>
        </Modal>
    );
};

// --- OVERTIME MODAL (PREMIUM SOLID WHITE UI - MATCHING BADLI MODAL) ---
const OvertimeModal = ({ onClose, onSave, worker, looms, handleVoiceClick }) => {
    const [selHour, setSelHour] = useState('05');
    const [selMin, setSelMin] = useState('00');
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // 🌟 Live Timer State
    const [liveTimer, setLiveTimer] = useState("00:00:00");
    
    // 🌟 Live Timer Effect (Har second update hone ke liye)
    useEffect(() => {
        if (worker.overtime != 1 || !worker.overtime_timestamp) return;
        const timer = setInterval(() => {
            const start = new Date(worker.overtime_timestamp);
            const now = new Date();
            let diffMs = now - start;
            if(diffMs < 0) diffMs = 0;
            
            const diffSecs = Math.floor(diffMs / 1000);
            const h = Math.floor(diffSecs / 3600);
            const m = Math.floor((diffSecs % 3600) / 60);
            const s = diffSecs % 60;
            
            setLiveTimer(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`);
        }, 1000);
        return () => clearInterval(timer);
    }, [worker.overtime_timestamp, worker.overtime]);

    const workerLoomList = useMemo(() => getLoomArray(worker.looms), [worker]);
    const workerLoomData = useMemo(() => {
        if (!looms || !Array.isArray(looms)) return [];
        return looms.filter(l => workerLoomList.includes(String(l.loom_id)));
    }, [looms, workerLoomList]);

    const totalProduction = useMemo(() => {
        return workerLoomData.reduce((acc, curr) => acc + (parseFloat(curr.meters) || 0), 0);
    }, [workerLoomData]);

    // 🌟 NAYA LOGIC: Overtime Strip Time Calculation (Same as Worker Box)
    const otDisplayData = useMemo(() => {
        let otStartDisplay = "";
        let otEndDisplay = "";
        let otDurationDisplay = "";
        
        if (worker.overtime == 1 && worker.shift_end && worker.overtime_hours) {
            let endMins = parseTimeStr(worker.shift_end);
            let addH = 0, addM = 0;
            
            if(String(worker.overtime_hours).includes(':')) { 
                [addH, addM] = worker.overtime_hours.split(':').map(Number); 
            } else { 
                addH = parseInt(worker.overtime_hours) || 0; 
            }
            
            let totalEndMins = endMins + (addH * 60) + addM;
            let endH = Math.floor(totalEndMins / 60) % 24;
            let endM = totalEndMins % 60;
            
            const ampm = endH >= 12 ? 'PM' : 'AM';
            let displayH = endH % 12 || 12;
            const displayM = endM < 10 ? '0'+endM : endM;
            
            otStartDisplay = formatTimeForDisplay(worker.shift_end);
            otEndDisplay = `${displayH}:${displayM} ${ampm}`;
            
           if (addM > 0) {
                otDurationDisplay = `${addH}:${String(addM).padStart(2, '0')} HOURS`;
            } else {
                otDurationDisplay = `${addH} HOURS`;
            }
        }
        return { otStartDisplay, otEndDisplay, otDurationDisplay };
    }, [worker]);

    const handleSubmit = async (e) => {
        if(e) e.preventDefault();
        if(parseInt(selHour) === 0 && parseInt(selMin) === 0) { alert(" Please select duration"); return; }
        
        if (parseInt(selHour) > 12 || (parseInt(selHour) === 12 && parseInt(selMin) > 0)) {
            const confirmMsg = ` चेतावनी: worker की पहले 12 Hours ड्यूटी हो चुकी है!\nअब ${selHour} Hrs ${selMin} Mins Overtime Duty ऐड करने से टोटल ड्यूटी 24 Hours से ज्यादा हो जाएगी।\n\nक्या आप सच में इसे allow करना चाहते हैं?`;
            if (!window.confirm(confirmMsg)) return; 
        }

        setIsSubmitting(true);
        const timeString = `${selHour}:${selMin}`;

        try {
            const res = await fetch(`${API_BASE}/update_worker_overtime.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: worker.id,
                    overtime: 1,
                    hours: timeString
                })
            });
            const data = await res.json();
            if(data.status === 'error') { alert(data.message); } else { onSave(); onClose(); }
        } catch(e) { console.error(e); alert("Network Error: " + e.message); }
        finally { setIsSubmitting(false); }
    };
    
    const handleStop = async (e) => {
        if(e) e.preventDefault();
        if(window.confirm(" क्या आप वाकई Overtime duty खत्म करना चाहते हैं?")) {
            setIsSubmitting(true);
            try {
                const res = await fetch(`${API_BASE}/update_worker_overtime.php`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ id: worker.id, overtime: 0 })
                });
                
                if (!res.ok) throw new Error(`Server Error: ${res.status}`);
                
                const data = await res.json();
                if(data.status === 'success') { onSave(); onClose(); }
                else { alert("Failed: " + (data.message || "Unknown error")); }
            } catch(e) { console.error(e); alert("Operation Failed: " + e.message); }
            finally { setIsSubmitting(false); }
        }
    };

    const hoursArray = Array.from({length: 25}, (_, i) => String(i).padStart(2, '0')); 
    const minutesArray = ['00', '15', '30', '45'];

    // --- EXACT BADLI MODAL STYLES ---
    const modalStyles = {
        premiumCard: { background: '#FFFFFF', padding: '16px', borderRadius: '16px', border: '1px solid rgba(226, 232, 240, 0.8)', boxShadow: '0 4px 15px -3px rgba(15, 23, 42, 0.03)', marginBottom: '12px', position: 'relative' },
        label: { fontSize: '9.5px', fontWeight: '800', color: '#64748B', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px', textTransform:'uppercase', letterSpacing: '0.6px' },
        input: { width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #E2E8F0', fontSize: '14px', outline: 'none', background: '#F8FAFC', color:'#0F172A', fontWeight:'800', boxSizing:'border-box', appearance: 'none', textAlign: 'center', cursor: 'pointer' },
        stickyBottom: { background: 'linear-gradient(145deg, #FFFFFF, #F8FAFC)', borderRadius: '16px', padding: '12px', boxShadow: '0 8px 25px -5px rgba(15, 23, 42, 0.05)', border: '1px solid rgba(226, 232, 240, 0.8)', marginTop: '16px', position: 'sticky', bottom: '0', zIndex: 10 },
        dangerSubmitBtn: { width: '100%', padding: '14px', background: 'linear-gradient(135deg, #DC2626, #EF4444)', color: '#FFFFFF', border: 'none', borderRadius: '12px', fontWeight: '900', cursor: 'pointer', fontSize:'11.5px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 6px 16px rgba(220, 38, 38, 0.25)', textTransform: 'uppercase', letterSpacing: '0.8px' },
        darkSubmitBtn: { width: '100%', padding: '14px', background: 'linear-gradient(135deg, #0F172A, #1E293B)', color: '#FFFFFF', border: 'none', borderRadius: '12px', fontWeight: '900', cursor: 'pointer', fontSize:'11.5px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 6px 16px rgba(15, 23, 42, 0.25)', textTransform: 'uppercase', letterSpacing: '0.8px' }
    };

    return (
        <Modal 
            isOpen={true} 
            onClose={(e) => { if (window.speechSynthesis) window.speechSynthesis.cancel(); onClose(e); }} 
            title={worker.overtime == 1 ? 'STOP OVERTIME' : 'SET OVERTIME'}
            icon={worker.overtime == 1 ? Clock : Zap} 
            iconColor={worker.overtime == 1 ? "#2563EB" : "#0F172A"} 
            iconBg={worker.overtime == 1 ? "#EFF6FF" : "#F8FAFC"}
            maxWidth="420px"
            bg="#FFFFFF" 
        >
            <style>{`
                ::-webkit-scrollbar { width: 4px; }
                ::-webkit-scrollbar-track { background: transparent; }
                ::-webkit-scrollbar-thumb { background: #E2E8F0; border-radius: 10px; }
            `}</style>
            
            <div style={{ marginTop: '0px', zoom: 0.95, width: '100%', boxSizing: 'border-box' }}>
                
                {worker.overtime == 1 ? (
                    // 🔴 ACTIVE OVERTIME (STOP VIEW)
                    <>
                        {/* 1. DURATION COMPACT STRIP (EXACTLY MATCHING WORKER BOX) */}     
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#EFF6FF', padding: '8px 12px', borderRadius: '10px', border: '1px solid #BFDBFE', boxShadow: '0 2px 6px rgba(37, 99, 235, 0.08)' }}>
                                <Zap size={14} color="#2563EB" />
                                <span style={{ fontSize: '11px', color: '#2563EB', fontWeight: '800', letterSpacing: '0.3px', display: 'flex', alignItems: 'center', textTransform: 'uppercase' }}>
                                    <span style={{ marginRight: '6px' }}>OVERTIME:</span>
                                    {otDisplayData.otStartDisplay}
                                    <span style={{ fontSize: '9px', opacity: 0.6, margin: '0 6px', fontWeight: '700' }}>TO</span>
                                    {otDisplayData.otEndDisplay}
                                    <span style={{ margin: '0 8px', color: '#93C5FD' }}>•</span>
                                    {otDisplayData.otDurationDisplay}
                                </span>
                            </div>
                        </div>
                        
                        {/* 2. TOTAL PRODUCTION CARD */}
                        <div style={{ ...modalStyles.premiumCard, textAlign: 'center' }}>
                            <label style={{...modalStyles.label, justifyContent: 'center', marginBottom: '6px'}}>
                                <Layers size={12} color="#64748B" /> TOTAL PRODUCTION
                            </label>
                            <div style={{ fontSize: '28px', fontWeight: '800', color: '#0F172A', marginTop: '4px' }}>
                                {totalProduction.toFixed(1)} <span style={{ fontSize: '13px', color: '#2563EB', fontWeight: '800' }}>METER</span>
                            </div>
                        </div>

                        {/* 3. LOOM WISE BREAKDOWN */}
                        <div style={{ ...modalStyles.premiumCard }}>
                            <label style={{...modalStyles.label}}>
                                <Factory size={12} color="#64748B" /> LOOM WISE BREAKDOWN
                            </label>
                            <div style={{ maxHeight: '140px', overflowY: 'auto', marginTop: '12px' }}>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                    {workerLoomData.map(l => {
                                        const isNightWorker = worker.shift === 'Night';
                                        const globalLoom = looms && looms.find(gl => String(gl.loom_id || gl.id).replace(/[^0-9]/g, '') === String(l.loom_id));
                                        const isActive = globalLoom ? isLoomActiveHelper(globalLoom.last_updated) : false;
                                        const hasProd = parseFloat(l.meters) > 0;
                                        
                                        return (
                                            <div key={l.loom_id} style={{ 
                                                flex: '1 1 calc(33.333% - 8px)', background: '#FFFFFF', borderRadius: '12px', padding: '8px 4px', 
                                                boxShadow: '0 4px 10px rgba(15, 23, 42, 0.03)', border: '1px solid #E2E8F0', 
                                                display: 'flex', flexDirection: 'column', minWidth: '0', position: 'relative', overflow: 'hidden'
                                            }}>                        
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', padding: '0 4px' }}>
                                                    <h3 style={{ fontSize: '9px', fontWeight: '900', color: '#94A3B8', margin: 0, letterSpacing: '0.2px' }}>LOOM {String(getLoomNumber(l.loom_id)).padStart(2, '0')}</h3>
                                                    {isActive ? (
                                                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#10B981', boxShadow: '0 0 4px rgba(16, 185, 129, 0.8)' }}></span>
                                                        </div>
                                                    ) : (
                                                        <span style={{ fontSize: '6px', fontWeight: '800', color: '#64748B', background: '#F1F5F9', padding: '2px 4px', borderRadius: '4px', letterSpacing: '0.5px' }}>OFF</span>
                                                    )}
                                                </div>
                                                
                                                <div style={{ display: 'flex', width: '100%', flex: 1, alignItems: 'flex-end' }}>
                                                    {hasProd ? (
                                                        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '8px 2px', borderRadius: '8px', transition: 'all 0.3s ease', 
                                                            background: isNightWorker ? '#0F172A' : '#F8FAFC', 
                                                            border: isNightWorker ? '1px solid #1E293B' : '1px solid #E2E8F0',
                                                            boxShadow: isNightWorker ? '0 4px 10px rgba(15, 23, 42, 0.15)' : 'none'
                                                        }}>
                                                            <div style={{ fontSize: '6px', fontWeight: '800', marginBottom: '4px', letterSpacing:'0.5px', color: isNightWorker ? '#94A3B8' : '#64748B' }}>{isNightWorker ? 'NIGHT SHIFT' : 'DAY SHIFT'}</div>
                                                            <div style={{ fontSize: '13px', fontWeight: '900', color: isNightWorker ? '#FFFFFF' : '#0F172A' }}>{parseFloat(l.meters).toFixed(1)}</div>
                                                        </div>
                                                    ) : (
                                                        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '8px 2px', borderRadius: '8px', border: '1px dashed #CBD5E1', background: '#F8FAFC' }}>
                                                            <div style={{ fontSize: '7px', fontWeight: '900', color: '#94A3B8', opacity: 0.7, letterSpacing: '0.5px' }}>NO PROD</div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {workerLoomData.length === 0 && (
                                        <span style={{fontSize:'10px', color:'#94A3B8', fontWeight:'700', textTransform: 'uppercase', width: '100%', textAlign: 'center', padding: '10px 0'}}>
                                            NO LOOMS ASSIGNED
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* STICKY BOTTOM ACTION AREA (Match Badli) */}
                        <div style={modalStyles.stickyBottom}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div style={{ background: '#FEE2E2', padding: '8px', borderRadius: '10px', border: '1px solid #FECACA' }}>
                                        <Clock size={16} color="#DC2626" strokeWidth={2.5} />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                                        <span style={{ fontSize: '12px', fontWeight: '800', color: '#0F172A', letterSpacing: '-0.2px', textTransform: 'uppercase' }}>
                                            OVERTIME ACTIVE
                                        </span>
                                        <span style={{ fontSize: '9px', color: '#64748B', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                            LIVE DURATION
                                        </span>
                                    </div>
                                </div>
                                {/* 🌟 LIVE TIMER DISPLAY WIDGET */}
                                <div style={{ display: 'flex', alignItems: 'center', background: '#FEF2F2', padding: '4px 8px', borderRadius: '6px', border: '1px solid #FECACA' }}>
                                    <span style={{ fontSize: '14px', fontWeight: '800', color: '#DC2626', letterSpacing: '1px', fontFamily: 'monospace' }}>
                                        {liveTimer}
                                    </span>
                                </div>
                            </div>
                            <button 
                                onClick={(e) => handleVoiceClick ? handleVoiceClick(e, " यह end Overtime Duty बटन है, इससे चल रही Overtime Duty खत्म कर के सेव सकते हैं।", handleStop, 'btn-ot-stop') : handleStop(e)} 
                                disabled={isSubmitting} 
                                style={modalStyles.dangerSubmitBtn}
                            >
                                <CheckCircle size={16} color="#FFFFFF" strokeWidth={2.5} />
                                <span>{isSubmitting ? 'SAVING...' : 'END OVERTIME DUTY & SAVE'}</span>
                            </button>
                        </div>
                    </>
                ) : (
                    // 🟢 SET OVERTIME (START VIEW)
                    <>
                        <div style={modalStyles.premiumCard}>
                            <label style={{...modalStyles.label, justifyContent: 'center', marginBottom: '16px'}}>
                                <Timer size={12} color="#64748B" /> SELECT DURATION
                            </label>
                            
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '16px', alignItems: 'center' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <select style={modalStyles.input} value={selHour} onChange={e => setSelHour(e.target.value)}>
                                        {hoursArray.map(h => <option key={h} value={h}>{h}</option>)}
                                    </select>
                                    <span style={{ fontSize: '9px', fontWeight: '800', color: '#64748B', marginTop: '10px', letterSpacing: '0.5px' }}>HOURS</span>
                                </div>
                                
                                <span style={{ fontSize: '24px', fontWeight: '800', color: '#0F172A', paddingBottom: '22px' }}>:</span>
                                
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <select style={modalStyles.input} value={selMin} onChange={e => setSelMin(e.target.value)}>
                                        {minutesArray.map(m => <option key={m} value={m}>{m}</option>)}
                                    </select>
                                    <span style={{ fontSize: '9px', fontWeight: '800', color: '#64748B', marginTop: '10px', letterSpacing: '0.5px' }}>MINS</span>
                                </div>
                            </div>
                        </div>

                        {/* STICKY BOTTOM ACTION AREA (Match Badli) */}
                        <div style={modalStyles.stickyBottom}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div style={{ background: '#F1F5F9', padding: '8px', borderRadius: '10px', border: '1px solid #E2E8F0' }}>
                                        <Zap size={16} color="#0F172A" strokeWidth={2.5} />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                                        <span style={{ fontSize: '12px', fontWeight: '800', color: '#0F172A', letterSpacing: '-0.2px', textTransform: 'uppercase' }}>
                                            {selHour}H {selMin}M SELECTED
                                        </span>
                                        <span style={{ fontSize: '9px', color: '#64748B', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                            TARGET DURATION
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <button 
                                onClick={(e) => handleVoiceClick ? handleVoiceClick(e, " यह start बटन है, इससे वर्कर का नया Overtime Duty शुरू हो जाएगा और सेव हो जाएगा।", handleSubmit, 'btn-ot-start') : handleSubmit(e)} 
                                disabled={isSubmitting} 
                                style={modalStyles.darkSubmitBtn}
                            >
                                <CheckCircle size={16} color="#FFFFFF" strokeWidth={2.5} />
                                <span>{isSubmitting ? 'STARTING...' : 'EXECUTE OVERTIME ASSIGNMENT'}</span>
                            </button>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};

// --- HELPER: CALCULATE EXACT TOTAL DAYS ---
const calculateTotalDays = (dojStr) => {
    const today = new Date();
    let start = dojStr ? new Date(dojStr) : new Date('2024-01-01');
    if (isNaN(start.getTime())) start = new Date('2024-01-01');
    
    start.setHours(0, 0, 0, 0);
    const current = new Date(today);
    current.setHours(0, 0, 0, 0);
    
    const diffTime = Math.abs(current - start);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; 
};

// --- WORKER CARD COMPONENT (DARK BLUE PREMIUM THEME) ---
const WorkerCard = ({ worker, index, looms, currentTime, onMenuToggle, isMenuOpen, onEditClick, onOvertimeClick, onEndBadliClick, onViewHistory, onToggleDuty, voiceAssist, handleVoiceClick, activeGuideFlow, onCancelBadgeClick }) => {
    const [showLooms, setShowLooms] = useState(false);
    const [timeFilter, setTimeFilter] = useState('today');
    const [workerLoomStats, setWorkerLoomStats] = useState(null);
    const [isLoomsLoading, setIsLoomsLoading] = useState(false);
    const timerRef = useRef(null);

    const isOnDuty = worker.on_duty == 1;

    // 🌟 100% REAL DATABASE CONNECTION FOR LOOM BOXES 🌟
    useEffect(() => {
        if (showLooms && !workerLoomStats) {
            setIsLoomsLoading(true);
            fetch(`${API_BASE}/get_worker_history.php?id=${worker.id}&t=${Date.now()}`)
            .then(res => res.json())
            .then(json => {
                if (json.status === 'success' && Array.isArray(json.data)) {
                    let loomMap = {}; 
                    const todayObj = new Date();
                    const todayDateStr = todayObj.toLocaleDateString('fr-CA'); 
                    
                    const currentDay = todayObj.getDay(); 
                    const diffToSat = (currentDay + 1) % 7; 
                    const startOfWeekObj = new Date(todayObj);
                    startOfWeekObj.setDate(todayObj.getDate() - diffToSat);
                    startOfWeekObj.setHours(0,0,0,0);
                    
                    const endOfWeekObj = new Date(startOfWeekObj);
                    endOfWeekObj.setDate(startOfWeekObj.getDate() + 6);
                    endOfWeekObj.setHours(23,59,59,999);

                  let validDojObj = null;
                    if (worker?.doj && worker.doj !== '0000-00-00' && worker.doj.trim() !== '') {
                        const parts = String(worker.doj).trim().split('-');
                        if (parts.length === 3) {
                            if (parts[0].length === 4) { 
                                validDojObj = new Date(parts[0], parseInt(parts[1]) - 1, parts[2]); // YYYY-MM-DD
                            } else { 
                                validDojObj = new Date(parts[2], parseInt(parts[1]) - 1, parts[0]); // DD-MM-YYYY
                            }
                        } else {
                            validDojObj = new Date(worker.doj);
                        }
                        if (!isNaN(validDojObj.getTime())) {
                            validDojObj.setHours(0, 0, 0, 0);
                        } else {
                            validDojObj = null; 
                        }
                    }
                    
                    json.data.forEach(e => {
                        const rawDate = e.date || e.entry_date || e.created_at || '';
                        if (!rawDate) return;
                        
                        const dateParts = rawDate.split(' ')[0];
                        const d = new Date(dateParts.replace(/-/g, '/'));
                        d.setHours(0, 0, 0, 0);
                        
                        if (validDojObj && d < validDojObj) return;
                        
                        const m = parseFloat(e.metersProduced || e.meters || e.total || e.production_meters || 0);
                        const lId = String(e.loom_id).replace(/[^0-9]/g, '');
                        if (!lId) return;

                        if (!loomMap[lId]) {
                            loomMap[lId] = { today: 0, week: 0, month: 0, total: 0 };
                        }

                        loomMap[lId].total += m;
                        if (dateParts === todayDateStr) {
                            loomMap[lId].today += m;
                        }
                        if (d >= startOfWeekObj && d <= endOfWeekObj) {
                            loomMap[lId].week += m;
                        }
                        if (d.getFullYear() === todayObj.getFullYear() && d.getMonth() === todayObj.getMonth()) {
                            loomMap[lId].month += m;
                        }
                    });
                    setWorkerLoomStats(loomMap);
                } else {
                    setWorkerLoomStats({});
                }
           })
            .catch(err => {
                console.error("Error fetching real loom stats:", err);
                setWorkerLoomStats({});
                alert("Network Error: Looms ki production data fetch nahi ho payi. Kripya internet connection check karein.");
            })
            .finally(() => setIsLoomsLoading(false));
        }
    }, [showLooms, worker.id, worker.doj]);
    
    const isOvertime = worker.overtime == 1;

    useEffect(() => {
        if (showLooms) {
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => { setShowLooms(false); }, 300000); 
        }
        return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    }, [showLooms]);

    const isActuallyOccupying = isWorkerOccupyingLooms(worker, currentTime);
    const isBadliAssigned = worker.is_badli_active == 1; 
    
    const checkBadliTime = () => {
         if (!isBadliAssigned) return false;
         return isWorkerOccupyingLooms(worker, currentTime) && !isOnDuty; 
    };
    
    const showBadliBanner = isBadliAssigned && checkBadliTime();
    const visualOnDuty = isActuallyOccupying; 

    const getWorkerTimeInfo = () => {
        if ((!worker.shift_start || !worker.shift_end) && !isBadliAssigned) return { text: "", durationText: "", remaining: null, color: "" };
        try {
            const now = new Date(currentTime);
            const nowSecsTotal = (now.getHours() * 3600) + (now.getMinutes() * 60) + now.getSeconds();
            
          let startStr = worker.shift_start;
            let endStr = worker.shift_end;

            if (worker.on_duty == 0 && worker.pending_shift_start && worker.pending_shift_end) {
                startStr = worker.pending_shift_start;
                endStr = worker.pending_shift_end;
            }

            if (isBadliAssigned && worker.badli_start && worker.badli_end) {
                startStr = worker.badli_start;
                endStr = worker.badli_end;
            }
            const startMins = parseTimeStr(startStr);
            const endMins = parseTimeStr(endStr);
            
            let diffMins = endMins - startMins;
            if (diffMins < 0) diffMins += 1440;
            const durH = Math.floor(diffMins / 60);
            const durM = diffMins % 60;
            const durationText = durM > 0 ? `${durH}:${String(durM).padStart(2, '0')} HOURS` : `${durH} HOURS`;
            
            let isActive = (endMins > startMins) ? 
                (nowSecsTotal/60 >= startMins && nowSecsTotal/60 < endMins) : 
                (nowSecsTotal/60 >= startMins || nowSecsTotal/60 < endMins);

            let otRemaining = null;
            let otActiveState = false;

            if (isOvertime) {
                let addH = 0, addM = 0;
                if(String(worker.overtime_hours).includes(':')) {
                    [addH, addM] = worker.overtime_hours.split(':').map(Number);
                } else { addH = parseInt(worker.overtime_hours); }
                
                let otEndMins = endMins + (addH * 60) + addM;
                if (otEndMins >= 1440) otEndMins -= 1440;
                
                let currentMins = nowSecsTotal / 60;
                if (otEndMins > endMins) {
                    if (currentMins >= endMins && currentMins < otEndMins) otActiveState = true;
                } else {
                    if (currentMins >= endMins || currentMins < otEndMins) otActiveState = true;
                }

                let endSecs = otEndMins * 60;
                
                if (endSecs < nowSecsTotal && (nowSecsTotal - endSecs > 43200)) endSecs += 86400; 
                else if (endSecs < nowSecsTotal) { }
                else if (nowSecsTotal > 43200 && endSecs < 43200) endSecs += 86400;

                let diffSecs = endSecs - nowSecsTotal;
                if(diffSecs < 0) diffSecs += 86400;

                if (diffSecs <= 600 && diffSecs > 0) { 
                    const m = Math.floor(diffSecs / 60);
                    const s = diffSecs % 60;
                    otRemaining = `Ends in ${m}:${s < 10 ? '0'+s : s}`;
                }
            }

            const formatCountdown = (secs) => {
                const m = Math.floor(secs / 60);
                const s = secs % 60;
                return `${m}:${s < 10 ? '0'+s : s}`;
            };

            let remainingText = null;
            let colorClass = "";
            
            let targetMins = isActive ? endMins : startMins;
            let targetSecs = targetMins * 60;
            if(targetSecs < nowSecsTotal) targetSecs += 86400;
            let diff = targetSecs - nowSecsTotal;

            if (isActive) { 
                colorClass = "#F87171"; // Adjusted for Dark Theme
                if(diff <= 600) remainingText = `Ends in ${formatCountdown(diff)}`;
            } else {
                colorClass = "#34D399"; // Adjusted for Dark Theme
                if(diff <= 600) remainingText = `Starts in ${formatCountdown(diff)}`;
            }

            return { 
                text: `${formatTimeForDisplay(startStr)} - ${formatTimeForDisplay(endStr)}`, 
                durationText: durationText,
                remaining: remainingText, color: colorClass, otRemaining: otRemaining, otActiveState: otActiveState
            };
        } catch (error) { return { text: "", durationText: "", remaining: null, color: "" }; }
    };

    const timeInfo = getWorkerTimeInfo();

  const renderInteractiveStatusBadge = () => {
        const isOvertimeActive = isOvertime && timeInfo.otActiveState;
        
        // 🌟 Premium Dark Theme adjustments for Badge
        let dotColor = '#94A3B8';
        let dotShadow = 'none';
        let textColor = '#94A3B8';
        let bgColor = 'rgba(255, 255, 255, 0.05)';
        let borderColor = 'rgba(255, 255, 255, 0.1)';
        let textLabel = 'OFF';
        let blinkClass = '';

        if (visualOnDuty) {
            if (isBadliAssigned) {
                dotColor = '#FBBF24'; dotShadow = '0 0 6px rgba(251, 191, 36, 0.8)'; textColor = '#FBBF24'; bgColor = 'rgba(245, 158, 11, 0.15)'; borderColor = 'rgba(245, 158, 11, 0.3)'; textLabel = 'BADLI'; blinkClass = 'blinkFast';
            } 
           else if (isOvertimeActive) {
                dotColor = '#38BDF8'; dotShadow = '0 0 6px rgba(56, 189, 248, 0.8)'; textColor = '#38BDF8'; bgColor = 'rgba(56, 189, 248, 0.15)'; borderColor = 'rgba(56, 189, 248, 0.3)'; textLabel = 'OVERTIME'; blinkClass = 'blinkFast';
            }
            else {
                dotColor = '#34D399'; dotShadow = '0 0 6px rgba(52, 211, 153, 0.8)'; textColor = '#34D399'; bgColor = 'rgba(16, 185, 129, 0.15)'; borderColor = 'rgba(16, 185, 129, 0.3)'; textLabel = 'ON'; blinkClass = 'blinkFast';
            }
        }

       return (
            <div onClick={(e) => { 
                e.stopPropagation(); 
                if(handleVoiceClick) handleVoiceClick(e, ` यह ड्यूटी स्टेटस है। अभी ${worker.name} की ड्यूटी, ${visualOnDuty ? ' ON है, यानी चालू' : ' OFF है, यानी बंद'} है।`, null, null); 
            }} 
            style={{display:'flex', alignItems:'center', gap:'4px', background: bgColor, padding:'4px 8px', borderRadius:'100px', border: `1px solid ${borderColor}`, cursor: 'default', boxShadow: '0 2px 4px rgba(0,0,0,0.2)', transition: 'all 0.3s ease'}}>
                <div style={{width:'6px', height:'6px', borderRadius:'50%', background: dotColor, boxShadow: dotShadow, animation: blinkClass ? `${blinkClass} 0.4s infinite` : 'none'}}></div>
                <span style={{fontSize:'9px', fontWeight:'800', color: textColor, letterSpacing:'0.5px'}}>{textLabel}</span>
            </div>
        );
    };

    const getBadliDateBoxes = () => {
        if(!worker.badli_from_date || !worker.badli_to_date) return null;
        const start = new Date(String(worker.badli_from_date).split(' ')[0]);
        const end = new Date(String(worker.badli_to_date).split(' ')[0]);
        
        if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;

        const dates = [];
        for (let dt = new Date(start); dt <= end; dt.setDate(dt.getDate() + 1)) { dates.push(new Date(dt)); }

        return (
            <div style={{display:'flex', gap:'3px', flexWrap:'wrap', marginTop:'0px'}}>
                {dates.map((date, index) => (
                    <div key={index} style={{
                        fontSize:'7px', fontWeight:'700', color:'#FFFFFF', 
                        background:'rgba(255,255,255,0.1)', padding:'1px 4px', 
                        borderRadius:'4px', border:'1px solid rgba(255,255,255,0.2)',
                        whiteSpace: 'nowrap'
                    }}>
                        [{date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) }]
                    </div>
                ))}
            </div>
        );
    };

    const getBadliDurationString = () => {
        if(!worker.badli_start || !worker.badli_end) return "";
        let startMins = parseTimeStr(worker.badli_start);
        let endMins = parseTimeStr(worker.badli_end);
        let diff = endMins - startMins;
        if(diff < 0) diff += 1440; 
        const h = Math.floor(diff / 60);
        const m = diff % 60;
        const duration = `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
        return `BADLI: ${duration} HOURS (${formatTimeForDisplay(worker.badli_start)} - ${formatTimeForDisplay(worker.badli_end)})`;
    };

    const badliTimeStr = getBadliDurationString();

   let otStartDisplay = "";
    let otEndDisplay = "";
    let otDurationDisplay = "";
    
    if (isOvertime && worker.shift_end && worker.overtime_hours) {
        let endMins = parseTimeStr(worker.shift_end);
        let addH = 0, addM = 0;
        
        if(String(worker.overtime_hours).includes(':')) { 
            [addH, addM] = worker.overtime_hours.split(':').map(Number); 
        } else { 
            addH = parseInt(worker.overtime_hours) || 0; 
        }
        
        let totalEndMins = endMins + (addH * 60) + addM;
        let endH = Math.floor(totalEndMins / 60) % 24;
        let endM = totalEndMins % 60;
        
        const ampm = endH >= 12 ? 'PM' : 'AM';
        let displayH = endH % 12 || 12;
        const displayM = endM < 10 ? '0'+endM : endM;
        
        otStartDisplay = formatTimeForDisplay(worker.shift_end);
        otEndDisplay = `${displayH}:${displayM} ${ampm}`;
        
       if (addM > 0) {
            otDurationDisplay = `${addH}:${String(addM).padStart(2, '0')} HOURS`;
        } else {
            otDurationDisplay = `${addH} HOURS`;
        }
    }

   const workerLoomList = getLoomArray(worker.looms);
    const assignedLooms = getLoomArray(worker.looms).map(id => String(id).replace(/[^0-9]/g, ''));
    const historicalLooms = workerLoomStats ? Object.keys(workerLoomStats).filter(id => workerLoomStats[id][timeFilter] > 0) : [];
    const combinedLoomIds = [...new Set([...assignedLooms, ...historicalLooms])].sort((a, b) => parseInt(a) - parseInt(b));
    
    const workerLoomData = combinedLoomIds.map(cleanId => {
        let exactMeters = 0;
        if (workerLoomStats && workerLoomStats[cleanId]) {
            exactMeters = workerLoomStats[cleanId][timeFilter] || 0;
        }
        return { loom_id: cleanId, meters: exactMeters };
    });

    const generateWorkerVoiceSummary = () => {
        const dutyStatus = worker.on_duty == 1 ? "on" : "off";
        let loomStr = " कोई लूम असाइन नहीं किया गया है";
        
        if (workerLoomList.length > 0) {
            const firstLoom = parseInt(workerLoomList[0].replace(/[^0-9]/g, ''), 10);
            const lastLoom = parseInt(workerLoomList[workerLoomList.length - 1].replace(/[^0-9]/g, ''), 10);
            if (workerLoomList.length === 1) {
                loomStr = ` लूम नंबर ${firstLoom} असाइन किया गया है`;
            } else {
                loomStr = ` लूम नंबर ${firstLoom} से लेकर लूम नंबर ${lastLoom} तक टोटल ${workerLoomList.length} लूम असाइन किए गए हैं`;
            }
        }
        
        let timeString = "";
        if (worker.shift_start && worker.shift_end) {
            const startHindi = formatHindiVoiceTime(worker.shift_start);
            const endHindi = formatHindiVoiceTime(worker.shift_end);
            const durationHindi = getDurationHindiText(worker.shift_start, worker.shift_end);
            timeString = ` ${worker.name} की ड्यूटी ${startHindi} से लेकर ${endHindi} तक टोटल ${durationHindi} की है`;
        } else {
            timeString = ` ${worker.name} की ड्यूटी का टाइम सेट नहीं है`;
        }
        
        const todayDate = new Date();
        const dateOptions = { day: 'numeric', month: 'long', year: 'numeric' };
        const dateString = todayDate.toLocaleDateString('hi-IN', dateOptions); 
        
        const production = parseFloat(worker.today || 0).toFixed(1);
        
        return ` ${worker.name} की ड्यूटी अभी, ${dutyStatus} है, ${worker.name} को ${loomStr}, ${timeString}, ${worker.name} ने आज ${dateString} को ${production} मीटर प्रोडक्शन किया है।`;
    };

    const getProductionVoice = () => {
        const todayDate = new Date();
        const dateOptions = { day: 'numeric', month: 'long', year: 'numeric' };
        const dateString = todayDate.toLocaleDateString('hi-IN', dateOptions);
        const production = parseFloat(worker.today || 0).toFixed(1);
        return `${worker.name} ने आज ${dateString} को ${production} मीटर प्रोडक्शन किया है।`;
    };

    const getLoomsVoice = () => {
        let loomStr = " कोई लूम असाइन नहीं किया गया है";
        if (workerLoomList.length > 0) {
            const firstLoom = parseInt(workerLoomList[0].replace(/[^0-9]/g, ''), 10);
            const lastLoom = parseInt(workerLoomList[workerLoomList.length - 1].replace(/[^0-9]/g, ''), 10);
            if (workerLoomList.length === 1) {
                loomStr = ` लूम नंबर ${firstLoom} असाइन किया गया है`;
            } else {
                loomStr = ` लूम नंबर ${firstLoom} से लेकर लूम नंबर ${lastLoom} तक टोटल ${workerLoomList.length} लूम असाइन किए गए हैं`;
            }
        }
        return ` यह लूम व्यू बटन है। ${worker.name} को ${loomStr}।`;
    };

    const getTimeVoice = () => {
        if (worker.shift_start && worker.shift_end) {
            const startHindi = formatHindiVoiceTime(worker.shift_start);
            const endHindi = formatHindiVoiceTime(worker.shift_end);
            const durationHindi = getDurationHindiText(worker.shift_start, worker.shift_end);
            return `${worker.name} की ड्यूटी ${startHindi} से लेकर ${endHindi} तक टोटल ${durationHindi} की है।`;
        } else {
            return `${worker.name} की ड्यूटी का टाइम सेट नहीं है।`;
        }
    };

    return (
      <div style={{ 
            background: '#0F172A', borderRadius: '24px', border: '1px solid rgba(255, 255, 255, 0.15)', 
            boxShadow: '0 20px 50px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.1)', marginBottom: '14px', position: 'relative', padding: 0,
            zIndex: activeGuideFlow === 'set_overtime' ? 100001 : (isMenuOpen ? 200 : 1),
            
            /* 🚀 BEHTREEN PERFORMANCE UPGRADE (BUTTER SMOOTH NO LAG) 🚀 */
            contentVisibility: 'auto',
            containIntrinsicSize: '250px',
            contain: 'paint layout',
            willChange: 'transform, opacity',
            transform: 'translateZ(0)',
            WebkitFontSmoothing: 'antialiased'
        }}>
            
          {/* TOP STRIP */}
            <div style={{padding: '12px 14px', background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '20px 20px 0 0', position: 'relative', zIndex: 5}}>
                
               {/* 3 DOTS MENU */}
                <div style={{position:'absolute', top: '12px', right:'14px', zIndex: 100}}>
                      <div id={`btn-worker-menu-${index}`} className="guide-ot-trigger" onClick={(e) => handleVoiceClick(e, " यह 3 डॉट्स मेनू बटन है, इससे आप वर्कर की डिटेल्स एडिट करने, बदली हटाने या Overtime Duty लगाने के ऑप्शन्स खोल सकते हैं।", () => onMenuToggle(e, worker.id), `menu-toggle-${worker.id}`, isMenuOpen, " मेनू पहले से ओपन है।")} style={{padding:'4px', cursor:'pointer', background:'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius:'8px', display:'flex', alignItems:'center', justifyContent:'center'}}>
                        <MoreVertical size={14} color="#FFFFFF" />
                    </div>
                     {isMenuOpen && (
                        <>
                            {/* Transparent Overlay */}
                            <div 
                                onClick={(e) => { e.stopPropagation(); onMenuToggle(e, worker.id); }} 
                                style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 998, cursor: 'default' }} 
                            />
                            
                            <div style={{position:'absolute', top:'100%', right:0, background:'#0F172A', boxShadow:'0 10px 30px rgba(0,0,0,0.5)', borderRadius:'12px', padding:'6px', width:'140px', zIndex:999, border:'1px solid rgba(255, 255, 255, 0.15)'}}>
                                
                                {/* 1. EDIT PROFILE OPTION */}
                                <div onClick={(e) => handleVoiceClick(e, " यह edit प्रोफाइल ऑप्शन है, इससे आप वर्कर की पर्सनल जानकारी और फोटो बदल सकते हैं।", () => onEditClick(e, worker), `menu-edit-${worker.id}||${worker.name}`)} style={{...styles.menuItem, background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)'}}>
                                    <div style={{width:'20px', height:'20px', borderRadius:'6px', background:'rgba(255,255,255,0.1)', display:'flex', alignItems:'center', justifyContent:'center'}}>
                                        <Edit size={10} color="#38BDF8"/>
                                    </div>
                                    <span style={{...styles.menuText, color:'#FFFFFF'}}>Edit Profile</span> 
                                 </div>
                                 
                                 {/* DIVIDER LINE */}
                                 <div style={{ height: '1px', background: 'rgba(255, 255, 255, 0.1)', margin: '2px 4px 6px 4px', borderRadius: '1px' }}></div>
                                 
                                 {/* 2. OVERTIME OPTIONS */}
                                 {!isOvertime ? (
                                     <div id={`menu-set-ot-${index}`} className="guide-ot-menu-item" onClick={(e) => handleVoiceClick(e, " यह सेट Overtime ऑप्शन है, इससे आप वर्कर को Overtime Duty पर लगा सकते हैं।", () => onOvertimeClick(e, worker), `menu-set-ot-${worker.id}||${worker.name}`)} style={{...styles.menuItem, background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)'}}>
                                       <div style={{width:'20px', height:'20px', borderRadius:'6px', background:'rgba(255,255,255,0.1)', display:'flex', alignItems:'center', justifyContent:'center'}}><Zap size={10} color='#38BDF8'/></div>
                                      <span style={{...styles.menuText, color:'#FFFFFF'}}>Set Overtime</span>
                                    </div>
                                 ) : timeInfo.otActiveState ? (
                                     <div onClick={(e) => handleVoiceClick(e, " यह end Overtime Duty ऑप्शन है, इससे चल रही Overtime Duty खत्म कर के सेव सकते हैं।", () => onOvertimeClick(e, worker), `menu-end-ot-${worker.id}`)} style={{...styles.menuItem, background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)'}}>
                                        <div style={{width:'20px', height:'20px', borderRadius:'6px', background:'rgba(255,255,255,0.1)', display:'flex', alignItems:'center', justifyContent:'center'}}><CheckSquare size={10} color='#38BDF8'/></div>
                                        <span style={{...styles.menuText, color:'#FFFFFF'}}>End Overtime Duty</span>
                                     </div>
                                 ) : (
                                     <div onClick={(e) => handleVoiceClick(e, " यह cancel Overtime Duty ऑप्शन है, इससे आप सेट कि हुइ Overtime Duty cancel कर सकते हैं।", () => onOvertimeClick(e, worker), `menu-cancel-ot-${worker.id}`)} style={{...styles.menuItem, background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)'}}>
                                        <div style={{width:'20px', height:'20px', borderRadius:'6px', background:'rgba(239,68,68,0.15)', display:'flex', alignItems:'center', justifyContent:'center'}}><X size={10} color='#F87171'/></div>
                                       <span style={{...styles.menuText, color:'#F87171'}}>Cancel Overtime Duty</span>
                                     </div>
                                 )}

                                 {/* 3. BADLI END OPTION */}
                                 {isBadliAssigned && (
                                     <div onClick={(e) => handleVoiceClick(e, " यह end बदली ड्यूटी ऑप्शन है, इससे चल रही बदली Duty खत्म कर के सेव कर सकते हैं।", () => onEndBadliClick(e, worker), `menu-end-badli-${worker.id}`)} style={{...styles.menuItem, background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', marginTop: '4px'}}>
                                        <div style={{width:'20px', height:'20px', borderRadius:'6px', background:'rgba(239,68,68,0.15)', display:'flex', alignItems:'center', justifyContent:'center'}}><UserMinus size={10} color='#F87171'/></div>
                                        <span style={{...styles.menuText, color:'#F87171'}}>End Badli Duty</span>
                                     </div>
                                 )}
                            </div>
                        </>
                      )}
                </div>

                <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start'}}>
                    {/* WORKER SUMMARY CLICK */}
                    <div onClick={(e) => handleVoiceClick(e, generateWorkerVoiceSummary(), () => onViewHistory(worker), `view-history-${worker.id}||${worker.name}`)} style={{cursor:'pointer', flex:1, display:'flex', alignItems:'center', gap:'10px'}}>
                        <div style={{width:'40px', height:'40px', borderRadius:'50%', background: '#0F172A', display:'flex', alignItems:'center', justifyContent:'center', border: '1px solid rgba(255, 255, 255, 0.15)', overflow:'hidden', flexShrink:0, opacity: isBadliAssigned ? 0.6 : 1, boxShadow:'0 2px 6px rgba(0,0,0,0.3)'}}>
                            {worker.profile_image ? <img src={worker.profile_image} alt="" style={{width:'100%', height:'100%', objectFit:'cover'}} /> : <span style={{fontSize:'13px', fontWeight:'700', color: '#FFFFFF'}}>{worker.name.charAt(0)}</span>}
                        </div>
                        <div style={{flex:1}}>
                          <div style={{display:'flex', flexDirection:'column', gap: '6px'}}>
                                
                                {/* WORKER NAME */}
                               <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                                    <div style={{
                                        fontSize: '15px', 
                                        fontWeight: '800', 
                                        color: isBadliAssigned ? '#64748B' : '#FFFFFF', 
                                        letterSpacing: '-0.3px', 
                                        textTransform: 'capitalize',
                                        textDecoration: isBadliAssigned ? 'line-through' : 'none',
                                        opacity: isBadliAssigned ? 0.6 : 1
                                    }}>
                                        {worker.name}
                                    </div>
                                    
                                </div>

                          {/* DYNAMIC DUTY STRIP LOGIC */}
                                {!isBadliAssigned && (() => {
                                    const now = new Date(currentTime);
                                    const nowMins = (now.getHours() * 60) + now.getMinutes();
                                    let oldDutyEnded = false;

                                    if (worker.shift_start && worker.shift_end) {
                                        let startMins = parseTimeStr(worker.shift_start);
                                        let endMins = parseTimeStr(worker.shift_end);
                                        if (endMins > startMins) {
                                            if (nowMins >= endMins || nowMins < startMins) oldDutyEnded = true;
                                        } else { 
                                            if (nowMins >= endMins && nowMins < startMins) oldDutyEnded = true;
                                        }
                                    } else {
                                        oldDutyEnded = true;
                                    }

                                    let isWithinOneWeek = true;
                                    if (worker.shift_updated_at) {
                                        const updatedDate = new Date(worker.shift_updated_at.replace(/-/g, '/'));
                                        const diffTime = Math.abs(now - updatedDate);
                                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                                        if (diffDays > 7) isWithinOneWeek = false;
                                    } else if (!worker.pending_shift_start) {
                                        isWithinOneWeek = false; 
                                    }
                                    
                                    const hasAnyPending = worker.pending_shift_start || worker.pending_rate || worker.pending_shift || worker.pending_looms;
                                    const showPendingAsMain = worker.pending_shift_start && worker.pending_shift_end && oldDutyEnded;
                                    const renderPendingStrip = worker.pending_shift_start && worker.pending_shift_end && !oldDutyEnded;
                                    const shouldShowGreenDot = (isWithinOneWeek || hasAnyPending) && !renderPendingStrip;
                                    const shouldShowNewDuty = (isWithinOneWeek || worker.pending_shift_start) && !renderPendingStrip;
                                    
                                    return (
                                        <>
                                            {renderPendingStrip && (() => {
                                                let pStart = parseTimeStr(worker.pending_shift_start);
                                                let pEnd = parseTimeStr(worker.pending_shift_end);
                                                let pDiff = pEnd - pStart;
                                                if (pDiff < 0) pDiff += 1440;
                                                let pH = Math.floor(pDiff / 60);
                                                let pM = pDiff % 60;
                                                let pendingDur = pM > 0 ? `${pH}:${String(pM).padStart(2, '0')} HOURS` : `${pH} HOURS`;

                                                return (
                                                    <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); if(onCancelBadgeClick) onCancelBadgeClick(worker, 'time', 'Shift Time'); }} 
     style={{ display: 'inline-flex', width: 'fit-content', alignItems: 'center', gap: '4px', background: 'rgba(52, 211, 153, 0.15)', padding: '4px 8px', borderRadius: '6px', border: '1px solid rgba(52, 211, 153, 0.3)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
    <Clock size={10} color="#34D399" style={{pointerEvents: 'none'}} /> 
    <span style={{ fontSize: '9px', color: '#34D399', fontWeight: '800', letterSpacing: '0.3px', pointerEvents: 'none', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                                                            <span style={{ marginRight: '4px' }}>NEW DUTY :</span>
                                                            {formatTimeForDisplay(worker.pending_shift_start)}
                                                            <span style={{ fontSize: '8px', opacity: 0.6, margin: '0 4px', fontWeight: '700', color: '#FFFFFF' }}>TO</span>
                                                            {formatTimeForDisplay(worker.pending_shift_end)}
                                                            <span style={{ margin: '0 6px', color: '#34D399', opacity: 0.5 }}>•</span>
                                                            {pendingDur}
                                                        </span> 
                                                        <X size={10} color="#34D399" style={{pointerEvents: 'none', marginLeft: '2px'}}/>
                                                    </div>
                                                );
                                            })()}

                                           <div style={{ position: 'relative', display: 'inline-flex', width: 'fit-content', alignItems: 'center', gap: '5px', background: 'rgba(255, 255, 255, 0.08)', padding: '4px 10px', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.15)', whiteSpace: 'nowrap' }}>
    {isWithinOneWeek && !renderPendingStrip && (
        <span style={{ position: 'absolute', top: '-3px', right: '-3px', width: '7px', height: '7px', backgroundColor: '#10B981', borderRadius: '50%', border: '1px solid #1E293B', boxShadow: '0 0 6px rgba(16, 185, 129, 0.8)' }}></span>
    )}
    <Clock size={11} color="#38BDF8" strokeWidth={2.5} />
    <span style={{ fontSize: '10px', color: '#38BDF8', fontWeight: '800', letterSpacing: '0.3px', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                                                    <span style={{ color: '#FFFFFF', marginRight: '4px' }}>{shouldShowNewDuty ? "NEW DUTY" : "DUTY"} :</span>
                                                    {showPendingAsMain ? (
                                                        <>
                                                            {formatTimeForDisplay(worker.pending_shift_start)}
                                                            <span style={{ fontSize: '9px', opacity: 0.6, margin: '0 4px', fontWeight: '700', color: '#FFFFFF' }}>TO</span>
                                                            {formatTimeForDisplay(worker.pending_shift_end)}
                                                        </>
                                                    ) : timeInfo.text ? (
                                                        timeInfo.text.includes('-') ? (
                                                            <>
                                                                {timeInfo.text.split('-')[0].trim()}
                                                                <span style={{ fontSize: '9px', opacity: 0.6, margin: '0 4px', fontWeight: '700', color: '#FFFFFF' }}>TO</span>
                                                                {timeInfo.text.split('-')[1].trim()}
                                                            </>
                                                        ) : timeInfo.text
                                                    ) : 'NO TIME SET'}

                                                    {timeInfo.durationText && (
                                                        <>
                                                            <span style={{ margin: '0 6px', color: '#38BDF8', opacity: 0.6 }}>•</span>
                                                            {showPendingAsMain ? (() => {
                                                                let diff = parseTimeStr(worker.pending_shift_end) - parseTimeStr(worker.pending_shift_start);
                                                                if (diff < 0) diff += 1440;
                                                                let h = Math.floor(diff / 60);
                                                                let m = diff % 60;
                                                                return m > 0 ? `${h}H ${String(m).padStart(2, '0')}M` : `${h} HOURS`;
                                                            })() : timeInfo.durationText}
                                                        </>
                                                    )}
                                                </span>
                                            </div>
                                        </>
                                    );
                                })()}

                                {/* OVERTIME STRIP */}
                                {isOvertime && otStartDisplay && (
                                    <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOvertimeClick(e, worker); }} 
                                        style={{ display: 'inline-flex', width: 'fit-content', alignItems: 'center', gap: '4px', background: 'rgba(56, 189, 248, 0.15)', padding: '4px 8px', borderRadius: '6px', border: '1px solid rgba(56, 189, 248, 0.3)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                        <Zap size={10} color="#38BDF8" style={{pointerEvents: 'none'}} />
                                        <span style={{ fontSize: '9px', color: '#38BDF8', fontWeight: '800', letterSpacing: '0.3px', pointerEvents: 'none', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                                            <span style={{ marginRight: '4px' }}>OVERTIME :</span>
                                            {otStartDisplay}
                                            <span style={{ fontSize: '8px', opacity: 0.6, margin: '0 4px', fontWeight: '700', color: '#FFFFFF' }}>TO</span>
                                            {otEndDisplay}
                                            <span style={{ margin: '0 6px', color: '#38BDF8', opacity: 0.6 }}>•</span>
                                            {otDurationDisplay}
                                        </span>
                                        <X size={12} color="#38BDF8" style={{pointerEvents: 'none', marginLeft: '2px'}} />
                                    </div>
                                )}
                                
                                {/* BADLI DETAILS */}
                                {isBadliAssigned && (
                                    <>
                                        <div style={{display:'flex', alignItems:'center', flexWrap:'wrap', gap:'4px'}}>
                                            <div style={{display:'flex', alignItems:'center', gap:'4px'}}>
                                                <ArrowRight size={12} color="#FBBF24" />
                                                <span style={{fontSize:'12px', fontWeight:'800', color:'#FBBF24', textTransform: 'uppercase', letterSpacing: '0.3px'}}>
                                                    {worker.badli_worker_name || 'SUBSTITUTE'} <span style={{fontSize: '9px', fontWeight: '700', opacity: 0.8}}>ACTIVE</span>
                                                </span>
                                            </div>
                                            {showBadliBanner && (
                                                <div style={{display:'flex', alignItems:'center', gap:'4px', marginLeft: '4px'}}>
                                                    {getBadliDateBoxes()}
                                                </div>
                                            )}
                                        </div>

                                       <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); onEndBadliClick(e, worker); }} 
                                            style={{ display: 'inline-flex', width: 'fit-content', alignItems: 'center', gap: '4px', background: 'rgba(245, 158, 11, 0.15)', padding: '4px 8px', borderRadius: '6px', border: '1px solid rgba(245, 158, 11, 0.3)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                            <Clock size={10} color="#FBBF24" style={{pointerEvents: 'none'}} />
                                            <span style={{ fontSize: '9px', color: '#FBBF24', fontWeight: '800', letterSpacing: '0.3px', pointerEvents: 'none', display: 'flex', alignItems: 'center', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                                                <span style={{ marginRight: '4px' }}>BADLI DUTY :</span>
                                                {formatTimeForDisplay(worker.badli_start)}
                                                <span style={{ fontSize: '8px', opacity: 0.6, margin: '0 4px', fontWeight: '700', color: '#FFFFFF' }}>TO</span>
                                                {formatTimeForDisplay(worker.badli_end)}
                                                <span style={{ margin: '0 6px', color: '#FBBF24', opacity: 0.6 }}>•</span>
                                                {(() => {
                                                    let bStart = parseTimeStr(worker.badli_start);
                                                    let bEnd = parseTimeStr(worker.badli_end);
                                                    let bDiff = bEnd - bStart;
                                                    if (bDiff < 0) bDiff += 1440;
                                                    let bH = Math.floor(bDiff / 60);
                                                    let bM = bDiff % 60;
                                                    return bM > 0 ? `${bH}:${String(bM).padStart(2,'0')} HOURS` : `${bH} HOURS`;
                                                })()}
                                            </span>
                                            <X size={12} color="#FBBF24" style={{pointerEvents: 'none', marginLeft: '2px'}} />
                                        </div>
                                    </>
                                )}

                                {/* PENDING UPDATES */}
                                <div style={{display:'flex', flexWrap:'wrap', gap:'4px'}}>
                                    {worker.pending_rate && (
                                        <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); if(onCancelBadgeClick) onCancelBadgeClick(worker, 'rate', 'New Rate'); }} 
                                             style={{background:'rgba(52, 211, 153, 0.15)', color:'#34D399', fontSize:'8px', fontWeight:'800', padding:'3px 6px', borderRadius:'6px', border: '1px solid rgba(52, 211, 153, 0.3)', display:'flex', alignItems:'center', gap:'4px', cursor: 'pointer'}}>
                                            <IndianRupee size={10} style={{pointerEvents: 'none'}} /> 
                                            <span style={{pointerEvents: 'none'}}>NEW RATE: ₹{worker.pending_rate}</span> 
                                            <X size={10} style={{pointerEvents: 'none'}}/>
                                        </div>
                                    )}

                                    {worker.pending_shift && (
                                        <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); if(onCancelBadgeClick) onCancelBadgeClick(worker, 'shift', 'New Shift (Day/Night)'); }} 
                                             style={{background:'rgba(167, 139, 250, 0.15)', color:'#A78BFA', fontSize:'8px', fontWeight:'800', padding:'3px 6px', borderRadius:'6px', border: '1px solid rgba(167, 139, 250, 0.3)', display:'flex', alignItems:'center', gap:'4px', cursor: 'pointer'}}>
                                            <Clock size={10} style={{pointerEvents: 'none'}} /> 
                                            <span style={{pointerEvents: 'none'}}>NEW SHIFT: {worker.pending_shift}</span> 
                                            <X size={10} style={{pointerEvents: 'none'}}/>
                                        </div>
                                    )}

                                    {worker.pending_looms && (
                                        <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); if(onCancelBadgeClick) onCancelBadgeClick(worker, 'looms', 'New Looms'); }} 
                                             style={{background:'rgba(244, 114, 182, 0.15)', color:'#F472B6', fontSize:'8px', fontWeight:'800', padding:'3px 6px', borderRadius:'6px', border: '1px solid rgba(244, 114, 182, 0.3)', display:'flex', alignItems:'center', gap:'4px', cursor: 'pointer'}}>
                                            <Layers size={10} style={{pointerEvents: 'none'}} /> 
                                            <span style={{pointerEvents: 'none'}}>NEW LOOMS: {worker.pending_looms}</span> 
                                            <X size={10} style={{pointerEvents: 'none'}}/>
                                        </div>
                                    )}
                                </div>
                                
                            </div>
                        </div>
                    </div>
                    
                   <div style={{marginRight:'36px', display:'flex', flexDirection:'column', alignItems:'flex-end'}}>
                        <div style={{marginBottom:'6px'}}>
                            {renderInteractiveStatusBadge()}
                        </div>

                     {/* REMAINING TIME TIMERS */}
                        <div onClick={(e) => { e.stopPropagation(); handleVoiceClick(e, getTimeVoice(), null); }} style={{cursor:'pointer', display:'flex', justifyContent:'flex-end', marginTop: '6px'}}>
                            {timeInfo.otRemaining ? (
                                <span style={{fontSize:'10px', fontWeight:'800', color: '#F87171', letterSpacing:'0.5px', textTransform:'uppercase'}}>
                                    {timeInfo.otRemaining}
                                </span>
                            ) : (
                                timeInfo.remaining && (
                                    <span style={{
                                        fontSize:'10px', fontWeight:'800', 
                                        color: timeInfo.remaining.includes('Ends') ? '#F87171' : '#34D399', 
                                        letterSpacing:'0.5px', textTransform:'uppercase'
                                    }}>
                                        {timeInfo.remaining}
                                    </span>
                                )
                            )}
                        </div>
                    </div>
                </div>
            </div>

          {/* BOTTOM SECTION - PREMIUM BOXES */}
            <div style={{padding: '0 12px 12px 12px', background: 'transparent', borderRadius: '0 0 20px 20px'}}>
                
                {/* 🌟 Rate Per Meter Badge 🌟 */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
                    {(() => {
                        let isRateUpdatedRecently = false;
                        if (worker.updated_at) {
                            const updatedDate = new Date(worker.updated_at.replace(/-/g, '/'));
                            const diffTime = Math.abs(new Date(currentTime) - updatedDate);
                            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                            if (diffDays <= 7) isRateUpdatedRecently = true;
                        }
                        
                        return (
                            <div style={{ 
                                background: isRateUpdatedRecently ? 'rgba(16, 185, 129, 0.15)' : 'rgba(255, 255, 255, 0.05)', 
                                border: isRateUpdatedRecently ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(255, 255, 255, 0.08)', 
                                borderBottom: 'none', 
                                borderRadius: '8px 8px 0 0', 
                                padding: '3px 10px', 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '4px', 
                                marginBottom: '-1px', 
                                zIndex: 2, 
                                position: 'relative',
                                transition: 'all 0.3s ease'
                            }}>
                                {isRateUpdatedRecently && (
                                    <span style={{ position: 'absolute', top: '-3px', right: '-3px', width: '7px', height: '7px', backgroundColor: '#10B981', borderRadius: '50%', border: '1px solid #1E293B', boxShadow: '0 0 6px rgba(16, 185, 129, 0.8)' }}></span>
                                )}
                                
                                <IndianRupee size={10} color={isRateUpdatedRecently ? "#34D399" : "#94A3B8"}/>
                                <span style={{ fontSize: '8px', fontWeight: '800', color: isRateUpdatedRecently ? '#34D399' : '#94A3B8', letterSpacing: '0.3px' }}>
                                    {isRateUpdatedRecently ? 'NEW RATE:' : 'RATE:'}
                                </span>
                                <span style={{ fontSize: '9px', fontWeight: '900', color: isRateUpdatedRecently ? '#10B981' : '#FFFFFF' }}>
                                    ₹{worker.rate_per_meter || worker.ratePerMeter || 0}/M
                                </span>
                            </div>
                        );
                    })()}
                </div>

                <div style={{ marginBottom: '14px', position: 'relative', zIndex: 1 }}>
                    
                    {/* 🌟 PREMIUM: TOGGLE BUTTONS (DARK THEME) 🌟 */}
                    <div style={{ 
                        display: 'flex', background: '#0F172A', padding: '6px', 
                        borderRadius: '16px 0 16px 16px', gap: '4px', border: '1px solid rgba(255, 255, 255, 0.08)', marginBottom: '10px',
                        boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.2)'
                    }}>
                        {['today', 'week', 'month', 'total'].map((filter) => {
                            const isActive = timeFilter === filter;
                            return (
                                <button key={filter} onClick={(e) => { e.stopPropagation(); setTimeFilter(filter); }} 
                                    style={{
                                        flex: isActive ? 1.2 : 1, padding: '8px 4px', 
                                        background: isActive ? 'linear-gradient(135deg, #0284C7 0%, #38BDF8 100%)' : 'transparent', 
                                        border: isActive ? '1px solid #38BDF8' : '1px solid transparent', 
                                        borderRadius: '12px', color: isActive ? '#FFFFFF' : '#94A3B8', 
                                        fontWeight: isActive ? '900' : '600', fontSize: '8px', cursor: 'pointer',
                                        boxShadow: isActive ? '0 4px 10px rgba(56, 189, 248, 0.3)' : 'none',
                                        transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
                                    }}>
                                    {filter === 'week' ? 'THIS WEEK' : filter === 'month' ? 'THIS MONTH' : filter.toUpperCase()}
                                </button>
                            );
                        })}
                    </div>

               {/* 🌟 PREMIUM: COMBINED PRODUCTION & EARNING BOX 🌟 */}
                    {(() => {
                        const currentProd = timeFilter === 'today' ? parseFloat(worker.today || 0) :
                                            timeFilter === 'week' ? parseFloat(worker.weekly || 0) :
                                            timeFilter === 'month' ? parseFloat(worker.monthly || 0) :
                                            parseFloat(worker.yearly || worker.total || 0);
                        
                        const rate = parseFloat(worker.rate_per_meter || worker.ratePerMeter || 0);
                        let currentEarn = 0;
                        
                        if (timeFilter === 'today') {
                            currentEarn = (worker.today_earning !== undefined && worker.today_earning !== null) ? parseFloat(worker.today_earning) : (currentProd * rate);
                        } else if (timeFilter === 'week') {
                            currentEarn = (worker.weekly_earning !== undefined && worker.weekly_earning !== null) ? parseFloat(worker.weekly_earning) : (currentProd * rate);
                        } else if (timeFilter === 'month') {
                            currentEarn = (worker.monthly_earning !== undefined && worker.monthly_earning !== null) ? parseFloat(worker.monthly_earning) : (currentProd * rate);
                        } else {
                            currentEarn = (worker.yearly_earning !== undefined && worker.yearly_earning !== null) ? parseFloat(worker.yearly_earning) : 
                                          ((worker.total_earning !== undefined && worker.total_earning !== null) ? parseFloat(worker.total_earning) : (currentProd * rate));
                        }
                        
                        const prodLabel = timeFilter === 'today' ? "TODAY'S PRODUCTION" : timeFilter === 'week' ? "THIS WEEK'S PRODUCTION" : timeFilter === 'month' ? "THIS MONTH'S PRODUCTION" : "TOTAL PRODUCTION";

                        const todayD = new Date();
                        let dateText = '';
                        if (timeFilter === 'today') {
                            dateText = todayD.toLocaleDateString('en-IN', {day:'numeric', month:'long', year:'numeric'}).toUpperCase();
                        } else if (timeFilter === 'week') {
                            const s = new Date(todayD); s.setDate(todayD.getDate() - ((todayD.getDay() + 1) % 7));
                            const e = new Date(s); e.setDate(s.getDate() + 6);
                            dateText = `${s.toLocaleDateString('en-IN', {day:'numeric', month:'long'}).toUpperCase()} TO ${e.toLocaleDateString('en-IN', {day:'numeric', month:'long'}).toUpperCase()}`;
                        } else if (timeFilter === 'month') {
                            const s = new Date(todayD.getFullYear(), todayD.getMonth(), 1);
                            const e = new Date(todayD.getFullYear(), todayD.getMonth() + 1, 0);
                            dateText = `${s.toLocaleDateString('en-IN', {day:'numeric', month:'long'}).toUpperCase()} TO ${e.toLocaleDateString('en-IN', {day:'numeric', month:'long'}).toUpperCase()}`;
                        } else {
                            let startStr = '1 JANUARY 2024';
                            if (worker.doj && worker.doj !== '0000-00-00') {
                                const d = new Date(String(worker.doj).replace(/-/g, '/'));
                                if (!isNaN(d)) startStr = d.toLocaleDateString('en-IN', {day:'numeric', month:'long', year:'numeric'}).toUpperCase();
                            }
                            const endStr = todayD.toLocaleDateString('en-IN', {day:'numeric', month:'long', year:'numeric'}).toUpperCase();
                            dateText = `${startStr} TO ${endStr}`;
                        }

                        let activeDaysDisplay = "0 ACTIVE DAYS";
                        if (timeFilter === 'today') {
                            activeDaysDisplay = currentProd > 0 ? "1 ACTIVE DAY" : "0 ACTIVE DAYS";
                        } else {
                            const daysVal = timeFilter === 'week' ? worker.weekly_active_days : 
                                            timeFilter === 'month' ? worker.monthly_active_days : 
                                            worker.total_active_days;
                            if (daysVal !== undefined && daysVal !== null && daysVal > 0) {
                                activeDaysDisplay = `${daysVal} ACTIVE DAYS`;
                            } else {
                                if (timeFilter === 'total') {
                                    if (currentProd > 0) {
                                        const totalDays = calculateTotalDays(worker.doj);
                                        activeDaysDisplay = `${totalDays} ACTIVE DAYS`;
                                    } else {
                                        activeDaysDisplay = "0 ACTIVE DAYS";
                                    }
                                } else {
                                    activeDaysDisplay = currentProd > 0 ? "ACTIVE" : "0 ACTIVE DAYS";
                                }
                            }
                        }

                        return (
                            <div style={{ background: '#1E293B', borderRadius: '16px', padding: '14px', border: '1px solid rgba(255, 255, 255, 0.12)', boxShadow: '0 8px 20px rgba(0, 0, 0, 0.4)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <div style={{ background: 'rgba(56, 189, 248, 0.15)', padding: '6px', borderRadius: '8px', border: '1px solid rgba(56, 189, 248, 0.3)' }}>
                                            <Layers size={14} color="#38BDF8"/>
                                        </div>
                                        <span style={{ fontSize: '9px', color: '#94A3B8', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{prodLabel}</span>
                                    </div>
                                    <span style={{ fontSize: '10px', color: '#38BDF8', fontWeight: '800', background: 'rgba(255, 255, 255, 0.08)', padding: '4px 10px', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.15)', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                                        <CalendarDays size={11} color="#38BDF8" strokeWidth={2.5} /> 
                                        {dateText.includes(' TO ') ? (
                                            <>
                                                {dateText.split(' TO ')[0]}
                                                <span style={{ fontSize: '9px', opacity: 0.6, margin: '0 4px', fontWeight: '700', color: '#FFFFFF' }}>TO</span>
                                                {dateText.split(' TO ')[1]}
                                            </>
                                        ) : dateText}
                                    </span>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', width: '100%', marginTop: '2px' }}>
                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                                        <span style={{ fontSize: '24px', fontWeight: '900', color: '#FFFFFF', lineHeight: '1' }}>{currentProd.toFixed(1)}</span>
                                        <span style={{ fontSize: '9px', color: '#38BDF8', fontWeight: '800', marginRight: '6px' }}>METER</span>
                                        <span style={{ fontSize: '18px', fontWeight: '900', color: '#34D399', lineHeight: '1' }}>₹{currentEarn.toFixed(0)}</span>
                                    </div>
                                    {timeFilter !== 'today' && (
                                        <span style={{ fontSize: '8px', color: '#34D399', fontWeight: '800', background: 'rgba(52, 211, 153, 0.15)', padding: '4px 8px', borderRadius: '6px', border: '1px solid rgba(52, 211, 153, 0.3)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                            <Activity size={10} color="#34D399" /> {activeDaysDisplay}
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })()}

                    {/* 🌟 PREMIUM: VIEW DETAILED REPORT BUTTON 🌟 */}
                    <div onClick={(e) => { e.stopPropagation(); onViewHistory(worker); }} 
                        style={{ marginTop: '12px', display: 'flex', justifyContent: 'center', cursor: 'pointer' }}>
                        <span style={{ background: 'rgba(56, 189, 248, 0.1)', border: '1px solid rgba(56, 189, 248, 0.3)', padding: '8px 16px', borderRadius: '100px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '9px', color: '#38BDF8', fontWeight: '900', boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)', transition: 'all 0.2s' }}>
                            VIEW DETAILED REPORT <ChevronRight size={14} color="#38BDF8" strokeWidth={3}/>
                        </span>
                    </div>
                </div>
            </div>

          {/* BOTTOM BAR: LOOMS (DARK THEME) */}
            <div 
                onClick={(e) => { e.stopPropagation(); handleVoiceClick(e, getLoomsVoice(), () => setShowLooms(!showLooms), `toggle-looms-${worker.id}||${worker.name}`); }}
                style={{
                    background: 'transparent', borderTop: '1px solid rgba(255, 255, 255, 0.08)', padding: '12px 14px',
                    cursor: 'pointer', display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    position: 'relative', zIndex: 4,
                    borderRadius: '0 0 20px 20px'
                }}
            >
               <div style={{fontSize:'11px', display:'flex', alignItems:'center', gap:'8px', flex:1, overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis', marginRight:'8px'}}>
                      <div style={{ background: 'rgba(255, 255, 255, 0.05)', padding: '4px', borderRadius: '6px' }}>
                          <Factory size={14} color="#FFFFFF" style={{flexShrink:0}}/> 
                      </div>
                      <span style={{fontSize: '10px', overflow:'hidden', textOverflow:'ellipsis', display: 'flex', alignItems: 'center', gap: '4px'}}>
                        <span style={{fontWeight: '900', color: '#FFFFFF', letterSpacing: '0.3px'}}>{getLoomArray(worker.looms).length} LOOMS:</span> 
                        <span style={{fontWeight: '700', color: '#94A3B8', letterSpacing: '0.2px'}}>{formatSimpleLoomList(worker.looms) || 'NONE'}</span>
                      </span>
                </div>
                <div style={{display:'flex', alignItems:'center', gap:'3px', flexShrink:0}}>
                    <span style={{fontSize: '9px', fontWeight: '800', color: '#38BDF8', textTransform:'uppercase'}}>
                        {showLooms ? 'HIDE' : 'VIEW'}
                    </span>
                    {showLooms ? <ChevronUp size={12} color="#38BDF8" strokeWidth={3}/> : <ChevronDown size={12} color="#38BDF8" strokeWidth={3}/>}
                </div>
            </div>

            {showLooms && (
                <div style={{position: 'relative', marginTop: '-10px', zIndex: 0}}>
                    {/* Dark Theme Chains */}
                    <div style={{position: 'absolute', top: 0, left: '16px', width: '5px', height: '20px', background: 'linear-gradient(to right, rgba(56, 189, 248, 0.4), rgba(56, 189, 248, 0.8), rgba(56, 189, 248, 0.4))', borderRadius: '3px', zIndex: 1, animation: 'chainExtend 0.3s ease-out'}}>
                        <div style={{width: '8px', height: '8px', background: '#38BDF8', borderRadius: '50%', position: 'absolute', top: '-4px', left: '-1px', boxShadow: '0 2px 6px rgba(56, 189, 248, 0.6)'}}></div>
                        <div style={{width: '8px', height: '8px', background: '#38BDF8', borderRadius: '50%', position: 'absolute', bottom: '-4px', left: '-1px', boxShadow: '0 2px 6px rgba(56, 189, 248, 0.6)'}}></div>
                    </div>
                    <div style={{position: 'absolute', top: 0, right: '16px', width: '5px', height: '20px', background: 'linear-gradient(to right, rgba(56, 189, 248, 0.4), rgba(56, 189, 248, 0.8), rgba(56, 189, 248, 0.4))', borderRadius: '3px', zIndex: 1, animation: 'chainExtend 0.3s ease-out'}}>
                        <div style={{width: '8px', height: '8px', background: '#38BDF8', borderRadius: '50%', position: 'absolute', top: '-4px', left: '-1px', boxShadow: '0 2px 6px rgba(56, 189, 248, 0.6)'}}></div>
                        <div style={{width: '8px', height: '8px', background: '#38BDF8', borderRadius: '50%', position: 'absolute', bottom: '-4px', left: '-1px', boxShadow: '0 2px 6px rgba(56, 189, 248, 0.6)'}}></div>
                    </div>
                    <div style={{background: '#0B1329', padding: '20px 10px 10px 10px', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '0 0 20px 20px', animation: 'swingIn 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275)', transformOrigin: 'top', marginTop: '8px', boxShadow: 'inset 0 4px 20px rgba(0,0,0,0.5)'}}>
                        {isLoomsLoading ? (
                            <div style={{textAlign: 'center', padding: '24px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px'}}>
                                <Activity size={20} color="#38BDF8" style={{ animation: 'pulse 1.5s infinite' }} />
                                <span style={{ fontSize: '9px', fontWeight: '800', color: '#94A3B8', letterSpacing: '1px' }}>FETCHING PRODUCTION...</span>
                            </div>
                        ) : workerLoomData.length > 0 ? (
                          <div style={{display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:'8px'}}>
                                {workerLoomData.map(l => {
                                    const isNightWorker = worker.shift === 'Night';
                                    const globalLoom = looms && looms.find(gl => String(gl.loom_id || gl.id).replace(/[^0-9]/g, '') === String(l.loom_id));
                                    const isActive = globalLoom ? isLoomActiveHelper(globalLoom.last_updated) : false;
                                    const hasProd = l.meters > 0;
                                    
                                    const isHistorical = !assignedLooms.includes(String(l.loom_id));
                                    
                                    return (
                                        <div key={l.loom_id} style={{ 
                                            background: '#1E293B', borderRadius: '12px', padding: '8px 4px', 
                                            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.3)', border: '1px solid rgba(255, 255, 255, 0.08)', 
                                            display: 'flex', flexDirection: 'column', minWidth: '0',
                                            transition: 'all 0.3s ease', position: 'relative', overflow: 'hidden',
                                            opacity: isHistorical ? 0.55 : 1
                                        }}>                        
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', padding: '0 4px' }}>
                                                <h3 style={{ fontSize: '9px', fontWeight: '900', color: '#94A3B8', margin: 0, letterSpacing: '0.2px' }}>LOOM {String(l.loom_id).padStart(2, '0')}</h3>
                                                {isActive ? (
                                                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#10B981', boxShadow: '0 0 6px rgba(16, 185, 129, 0.8)' }}></span>
                                                    </div>
                                                ) : (
                                                    <span style={{ fontSize: '6px', fontWeight: '800', color: '#FFFFFF', background: 'rgba(255,255,255,0.1)', padding: '2px 4px', borderRadius: '4px', letterSpacing: '0.5px' }}>OFF</span>
                                                )}
                                            </div>
                                            
                                            <div style={{ display: 'flex', width: '100%', flex: 1, alignItems: 'flex-end' }}>
                                                {hasProd ? (
                                                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '8px 2px', borderRadius: '8px', transition: 'all 0.3s ease', 
                                                        background: '#0F172A', 
                                                        border: '1px solid rgba(255, 255, 255, 0.1)',
                                                        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.2)'
                                                    }}>
                                                        <div style={{ fontSize: '6px', fontWeight: '800', marginBottom: '4px', letterSpacing:'0.5px', color: '#38BDF8' }}>{isNightWorker ? 'NIGHT SHIFT' : 'DAY SHIFT'}</div>
                                                        <div style={{ fontSize: '13px', fontWeight: '900', color: '#FFFFFF' }}>{l.meters.toFixed(1)}</div>
                                                    </div>
                                                ) : (
                                                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '8px 2px', borderRadius: '8px', border: '1px dashed rgba(255,255,255,0.2)', background: 'transparent' }}>
                                                        <div style={{ fontSize: '7px', fontWeight: '900', color: '#64748B', letterSpacing: '0.5px' }}>NO PROD</div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div style={{textAlign: 'center', padding: '16px', fontSize: '10px', fontWeight: '800', color: '#94A3B8'}}>
                                NO LOOMS ASSIGNED OR PRODUCTION
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// --- MAIN WRAPPER ---
const Workers = ({ onBack, workers, looms, refreshData, globalGuide }) => {
    // --- SMART GUIDE INITIALIZATION ---
    const {
        voiceAssist, showGuideModal, setShowGuideModal,
        activeGuideFlow, currentGuideMessage,
        toggleVoiceAssist, handleVoiceClick,
        handleFocusVoice, handleSelectVoice, handleTextBlurVoice, // 👇 ये लाइन यहाँ जोड़नी है
        startSmartGuide, endSmartGuide, advanceNextStep,
        speakHinglish
    } = globalGuide;

   const [subView, setSubView] = useState('list');
    const [showOvertimeModal, setShowOvertimeModal] = useState(null);

    // 🌟 NAYA: Header Menu (Dropdown) ke liye State aur logic
    const [isHeaderMenuOpen, setIsHeaderMenuOpen] = useState(false);
    const headerMenuRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isHeaderMenuOpen && headerMenuRef.current && !headerMenuRef.current.contains(event.target)) {
                setIsHeaderMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isHeaderMenuOpen]);

    const [showBadliModal, setShowBadliModal] = useState(false);
    const [showBadliEndModal, setShowBadliEndModal] = useState(null); 
    const [showModal, setShowModal] = useState(false);
    const [editingWorker, setEditingWorker] = useState(null);
    const [switchConfirm, setSwitchConfirm] = useState(null);
    const [selectedWorkerHistory, setSelectedWorkerHistory] = useState(null);
    const [ownerProfile, setOwnerProfile] = useState(null);
    const [activeShiftView, setActiveShiftView] = useState('Day');
    const [currentTime, setCurrentTime] = useState(Date.now());
    const hasSetInitialShift = useRef(false);
  
    const [menuOpenId, setMenuOpenId] = useState(null);
    const hasOnDutyWorker = workers.some(w => w.on_duty == 1);

    // 🌟 NAYA: Badge Cancel karne ka state aur functions 🌟
    const [cancelBadgeConfirm, setCancelBadgeConfirm] = useState(null);
    const [isCancelling, setIsCancelling] = useState(false); // 🔥 NAYA LOADING STATE 🔥

    const handleCancelBadgeInit = (worker, fieldType, label) => {
        setCancelBadgeConfirm({ worker, fieldType, label });
    };

    const processCancelBadge = async () => {
        if (!cancelBadgeConfirm) return;
        setIsCancelling(true); // Button Processing Mode ON
        const { worker, fieldType } = cancelBadgeConfirm;
        
        try {
            const res = await fetch(`${API_BASE}/cancel_pending.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: worker.id, type: fieldType })
            });
            
            // Server error catch karne ka smart tareeqa
            const textData = await res.text();
            try {
                const data = JSON.parse(textData);
                if (data.status === 'success') {
                    // 🌟 INSTANT UI UPDATE
                    if (fieldType === 'time') {
                        worker.pending_shift_start = null;
                        worker.pending_shift_end = null;
                    } else if (fieldType === 'rate') {
                        worker.pending_rate = null;
                    } else if (fieldType === 'shift') {
                        worker.pending_shift = null;
                    } else if (fieldType === 'looms') {
                        worker.pending_looms = null;
                    }
                    setCancelBadgeConfirm(null);
                    if(refreshData) refreshData(); // Data Refersh karega
                } else {
                    alert("Failed to cancel: " + data.message);
                }
            } catch(jsonErr) {
                console.error("PHP Output Error:", textData);
                alert("Server Error! Kripya 'cancel_pending.php' file check karein.");
            }
        } catch(e) {
            console.error("Cancel Error:", e);
            alert("Network Error: Internet connection check karein.");
        } finally {
            setIsCancelling(false); // Button Processing Mode OFF
        }
    };

    const sortedWorkers = useMemo(() => {
        if (!Array.isArray(workers)) return [];
        return [...workers].sort((a, b) => parseInt(a.id) - parseInt(b.id));
    }, [workers]);

    useEffect(() => {
        const timer = setInterval(() => {
            fetch(`${API_BASE}/auto_update_duty.php`)
                .then(() => refreshData())
                .catch(err => console.error("Auto update failed", err));
        }, 60000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        if (!ownerProfile) return;
        if (!hasSetInitialShift.current) {
            const parseTimeStr = (str) => {
                try {
                    if (!str) return null;
                    const parts = str.trim().split(' ');
                    if(parts.length < 2) return null;
                    const [h, m] = parts[0].split(':').map(Number);
                    const period = parts[1].toUpperCase(); 
                    let hr = h; 
                    if (period === 'PM' && h !== 12) hr += 12; 
                    if (period === 'AM' && h === 12) hr = 0;
                    return hr * 60 + m;
                } catch (err) { return null; }
            };
            const now = new Date();
            const nowMins = now.getHours() * 60 + now.getMinutes();
            let dayStartStr = ownerProfile.shift_day_time ? ownerProfile.shift_day_time.split(' - ')[0] : '08:00 AM';
            let dayEndStr = ownerProfile.shift_day_time ? ownerProfile.shift_day_time.split(' - ')[1] : '08:00 PM';
            let dayStart = parseTimeStr(dayStartStr);
            let dayEnd = parseTimeStr(dayEndStr);
            if (dayStart === null || dayEnd === null) {
                setActiveShiftView('Day');
            } else {
                let detectedShift = 'Day';
                if (dayEnd > dayStart) {
                    if (nowMins >= dayStart && nowMins < dayEnd) detectedShift = 'Day'; else detectedShift = 'Night';
                } else {
                    if (nowMins >= dayStart || nowMins < dayEnd) detectedShift = 'Day'; else detectedShift = 'Night';
                }
                setActiveShiftView(detectedShift);
            }
            hasSetInitialShift.current = true;
        }
        const timer = setInterval(() => { setCurrentTime(Date.now()); }, 1000);
        return () => clearInterval(timer);
    }, [ownerProfile]);

    const visibleWorkers = useMemo(() => {
        return sortedWorkers.filter(w => w.shift === activeShiftView);
    }, [sortedWorkers, activeShiftView]);

    const regularWorkers = visibleWorkers.filter(w => w.is_badli == 0);
    const badliWorkers = visibleWorkers.filter(w => w.is_badli == 1); 

    useEffect(() => {
        fetch(`${API_BASE}/get_owner_profile.php`)
            .then(res => res.json()).then(json => { if(json.status === 'success') setOwnerProfile(json.data); }).catch(e => console.log(e));
    }, []);

    const handleToggleCheck = (e, targetWorker) => {
        if (targetWorker.is_badli_active == 1) {
            alert(" बदली ड्यूटी एक्टिव है! इसे बंद करने के लिए 3-डॉट्स मेनू -> 'End Badli Duty' का इस्तेमाल करें।");
            return;
        }

        if (targetWorker.on_duty == 1) { updateDutyStatus(targetWorker, 0); return; }
        
        const targetLooms = getLoomArray(targetWorker.looms);
        const conflictingWorker = workers.find(w => {
            if (String(w.id) === String(targetWorker.id)) return false; 
            if (!isWorkerOccupyingLooms(w, currentTime)) return false; 
            const currentLooms = getLoomArray(w.looms);
            return targetLooms.some(l => currentLooms.includes(l));
        });
        if (conflictingWorker) {
            setSwitchConfirm({ active: conflictingWorker, new: targetWorker });
        } else {
            updateDutyStatus(targetWorker, 1);
        }
    };

    const confirmSwitch = async () => { 
        if (!switchConfirm) return; 
        await updateDutyStatus(switchConfirm.active, 0, false); 
        await updateDutyStatus(switchConfirm.new, 1, true); 
        setSwitchConfirm(null); 
    };
    
    const updateDutyStatus = async (worker, status, shouldRefresh = true) => { 
        // 🌟 NAYA LOGIC: INSTANT UI UPDATE 🌟
        if (status === 0) {
            // 🛑 DUTY OFF: Main fields me naya data daalo, par Badges mat hatao!
            worker.on_duty = 0;
            if (worker.pending_looms) worker.looms = worker.pending_looms;
            if (worker.pending_shift) worker.shift = worker.pending_shift;
            if (worker.pending_shift_start) {
                worker.shift_start = worker.pending_shift_start;
                worker.shift_end = worker.pending_shift_end;
            }
            if (worker.pending_rate) worker.ratePerMeter = worker.pending_rate;
            
            // Note: Humne pending fields ko null NAHI kiya, taaki badges waise hi chamakte rahein.
            
        } else if (status === 1) {
            // 🟢 DUTY ON: Agli duty shuru ho gayi! Ab Pending details ko asli details bana do aur Badges hata do.
            worker.on_duty = 1;
            if (worker.pending_looms) worker.looms = worker.pending_looms;
            if (worker.pending_shift) worker.shift = worker.pending_shift;
            if (worker.pending_shift_start) {
                worker.shift_start = worker.pending_shift_start;
                worker.shift_end = worker.pending_shift_end;
            }
            if (worker.pending_rate) worker.rate_per_meter = worker.pending_rate; // UI update for earnings
            
            worker.pending_looms = null;
            worker.pending_shift = null;
            worker.pending_shift_start = null;
            worker.pending_shift_end = null;
            worker.pending_rate = null;
        }

        try { 
            const response = await fetch(`${API_BASE}/update_worker_status.php`, { 
                method: 'POST', 
                headers: {'Content-Type': 'application/json'}, 
                body: JSON.stringify({ id: worker.id, on_duty: status, shift: worker.shift }) 
            }); 
            const data = await response.json();
            
            if (data.status === 'error') {
                alert(data.message);
                return; 
            }
            
            if (shouldRefresh) refreshData(); 
        } catch (e) { console.error("Error", e); } 
    };

    const handleEndBadliClick = (e, worker) => {
        setMenuOpenId(null);
        setShowBadliEndModal(worker);
    };

    const processEndBadli = async (worker) => {
        try { 
            const formData = new FormData();
            formData.append('id', worker.id);
            formData.append('is_active', 0);

            const res = await fetch(`${API_BASE}/update_worker_badli.php`, { 
                method: 'POST', 
                body: formData 
            });
           const data = await res.json();
            if (data.status === 'success') {
                refreshData();
            } else {
                alert("Failed: " + data.message);
            }
        } catch(e) { 
            console.error(e); 
            alert("Network Error: Badli Duty end nahi ho payi, kripya internet connection check karein.");
        }
    };

    const handleMenuToggle = (e, workerId) => {
        setMenuOpenId(prev => prev === workerId ? null : workerId);
    };

    const handleOvertimeClick = (e, worker) => { 
    setMenuOpenId(null); 

    // 1. Agar Overtime pehle se chal raha hai, toh seedha modal kholo (Stop karne ke liye)
    if (worker.overtime == 1) { 
        setShowOvertimeModal(worker); 
        return; 
    }

    // 2. ADVANCE CHECK: Kya worker abhi duty par ACTIVE hai?
    // User requirement: Worker active hona chahiye tabhi OT set hoga
    if (worker.on_duty != 1) {
        const activeError = `${worker.name} abhi duty par active nahi hai. Overtime set karne ke liye pehle worker ki duty ON honi chahiye.`;
        speakHinglish(activeError); 
        alert(activeError);
        return;
    }

    // 3. SHIFT TIME CALCULATION
    const now = new Date();
    const nowMins = now.getHours() * 60 + now.getMinutes();
    const start = parseTimeStr(worker.shift_start);
    const end = parseTimeStr(worker.shift_end);
    
    // Shift ke andar hai ya nahi check karein
    let isDutyTime = (end > start) 
        ? (nowMins >= start && nowMins < end) 
        : (nowMins >= start || nowMins < end);
    
   
    
    // Sab kuch sahi hai toh modal dikhayein
    setShowOvertimeModal(worker); 
};

    const handleEditProfileClick = (e, worker) => {
        setMenuOpenId(null);
        setEditingWorker(worker); setShowModal(true);
    };

    const handleViewHistory = (w) => { setSelectedWorkerHistory(w); setSubView('history'); };

    // 🌟 NAYA LOGIC: Check all Workers Modals
    const isAnyModalOpen = showGuideModal || showOvertimeModal || showBadliModal || showBadliEndModal || showModal || switchConfirm || cancelBadgeConfirm || menuOpenId;

    // ===== 🌟 PREMIUM SCROLL LOCK (Hostinger / Mobile Fix) 🌟 =====
    useEffect(() => {
        if (isAnyModalOpen) {
            document.body.style.overflow = 'hidden'; 
        } else {
            document.body.style.overflow = 'auto';   
        }
        return () => { document.body.style.overflow = 'auto'; };
    }, [isAnyModalOpen]);

    if (subView === 'history' && selectedWorkerHistory) { 
    return ( <WorkerHistory worker={selectedWorkerHistory} onBack={() => setSubView('list')} availableLooms={looms} refreshData={refreshData} ownerProfile={ownerProfile} globalGuide={globalGuide} /> );
}

    return (
        <div style={{...styles.container, paddingBottom: '20px'}}>
            {menuOpenId && <div style={{position:'fixed', top:0, left:0, width:'100%', height:'100%', zIndex:90, background:'transparent'}} onClick={() => setMenuOpenId(null)} />}
            
           {/* ===== 🌟 PREMIUM HEADER (DARK THEME) 🌟 ===== */}
            <div style={{...styles.header, background: '#0F172A', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', boxShadow: '0 4px 20px rgba(0,0,0,0.3)', zIndex: activeGuideFlow ? 20 : 1000}}>
                <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
                    <button onClick={(e) => handleVoiceClick(e, " यह बैक बटन है, इससे आप पिछले पेज पर वापस जा सकते हैं।", onBack, 'btn-back')} style={{...styles.backBtn, background: '#1E293B', border: '1px solid rgba(255, 255, 255, 0.12)', boxShadow: '0 2px 8px rgba(0,0,0,0.2)'}}>
                        <ArrowLeft size={18} color="#FFFFFF"/>
                    </button>
                    <div>
                        <h1 style={{fontSize:'16px', fontWeight:'700', color:'#FFFFFF', margin:0, letterSpacing: '-0.5px'}}>Workers</h1>
                        <div style={{fontSize:'8px', color:'#94A3B8', fontWeight:'600', marginTop:'2px', letterSpacing:'0.5px', textTransform:'uppercase'}}>
                            {formatFullDateTime(currentTime)}
                        </div>
                    </div>
                </div>
                
              <div style={{display:'flex', gap:'8px', alignItems:'center'}}>
    
    {/* SMART GUIDE MODAL TRIGGER */}
    <button 
        onClick={(e) => handleVoiceClick(e, " यह स्मार्ट गाइड मेनू है, इसमें आपको ऐप चलाने की लाइव ट्रेनिंग मिलेगी।", () => setShowGuideModal(true), 'btn-guide-menu')} 
        style={{
            padding: '6px 8px', borderRadius: '10px',
            background: '#1E293B', border: '1px solid rgba(255, 255, 255, 0.12)',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s'
        }}
    >
        <HelpCircle size={14} color="#38BDF8"/>
    </button>

   {/* 🌟 HAMBURGER MENU (DROPDOWN) 🌟 */}
<div ref={headerMenuRef} style={{ position: 'relative' }}>
    <button 
        onClick={() => setIsHeaderMenuOpen(!isHeaderMenuOpen)} 
        style={{ 
            width: '28px', height: '28px', borderRadius: '8px', 
            background: isHeaderMenuOpen ? '#334155' : '#1E293B', border: '1px solid rgba(255, 255, 255, 0.12)', 
            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)', transition: 'all 0.2s'
        }}
    >
        <Menu size={16} color="#FFFFFF" />
    </button>

    {isHeaderMenuOpen && (
        <>
            <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 99 }} onClick={() => setIsHeaderMenuOpen(false)} />
            
            <div className="animate-in" style={{ position: 'absolute', top: '120%', right: 0, background: '#0F172A', border: '1px solid rgba(255, 255, 255, 0.15)', borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.5)', padding: '6px', display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '180px', zIndex: 100 }}>
                
                {/* 1. BADLI BUTTON */}
                <button id="btn-badli" onClick={(e) => {
                    setIsHeaderMenuOpen(false);
                    handleVoiceClick(e, " यह बदली बटन है, अगर कोई वर्कर छुट्टी पर है, तो उसकी जगह दूसरे वर्कर को ड्यूटी पर लगाने के लिए इसका इस्तेमाल होता है।", () => setShowBadliModal(true), 'btn-badli');
                }} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', borderRadius: '8px', background: 'transparent', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left' }}>
                    <UserX size={14} color="#94A3B8" />
                    <span style={{ fontSize: '11px', fontWeight: '600', color: '#94A3B8' }}>Assign Badli</span>
                </button>

                {/* DIVIDER */}
                <div style={{ height: '1px', background: 'rgba(255, 255, 255, 0.08)', margin: '2px 4px', borderRadius: '1px' }}></div>

                {/* 2. ADD WORKER BUTTON */}
                <button id="btn-add-worker" onClick={(e) => {
                    setIsHeaderMenuOpen(false);
                    handleVoiceClick(e, " यह ऐड वर्कर बटन है, इससे आप अपनी टेक्सटाइल कारखाने में नया वर्कर जोड़ सकते हैं।", () => { setEditingWorker(null); setShowModal(true); }, 'btn-add-worker');
                }} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', borderRadius: '8px', background: 'transparent', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left' }}>
                    <UserPlus size={14} color="#34D399" />
                    <span style={{ fontSize: '11px', fontWeight: '600', color: '#34D399' }}>Add New Worker</span>
                </button>

            </div>
        </>
    )}
</div>
</div>
            </div>

         <div style={styles.content}>

            {/* 🌟 DARK BLUE PREMIUM CONTAINER BOX (SCREENSHOT MATCHED EXACT UI) 🌟 */}
<div className="animate-in" style={{ 
    background: '#0F172A', 
    padding: '12px 14px', 
    borderRadius: '20px', 
    border: '1px solid rgba(255, 255, 255, 0.12)', 
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.4)',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '16px'
}}>
   {/* 1. TOTAL STAFF & ON DUTY STRIP (50-50 EQUAL SPLIT & ULTRA COMPACT) */}
    <div 
        onClick={(e) => handleVoiceClick && handleVoiceClick(e, ` आपके पास टोटल ${workers.length} वर्कर का स्टाफ हैं, जिनमें से ${workers.filter(w=>w.on_duty==1).length} ड्यूटी पर एक्टिव हैं।`, null)}
        style={{ 
            background: '#1E293B', 
            borderRadius: '8px', 
            padding: '3px 6px', 
            width: '100%',
            display: 'grid', 
            gridTemplateColumns: '1fr auto 1fr', 
            alignItems: 'center', 
            border: '1px solid rgba(255, 255, 255, 0.08)',
            cursor: 'pointer',
            boxSizing: 'border-box'
        }}
    >
        {/* LEFT 50% PART: TOTAL STAFF */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', lineHeight: 1 }}>
            <User size={12} color="#38BDF8" strokeWidth={2.5} style={{ display: 'block' }} />
            <span style={{ fontSize: '10px', fontWeight: '800', color: '#94A3B8', letterSpacing: '0.5px', lineHeight: 1 }}>
                TOTAL STAFF:
            </span>
            <span style={{ fontSize: '12px', fontWeight: '900', color: '#FFFFFF', lineHeight: 1 }}>
                {workers.length}
            </span>
        </div>

        {/* CENTER DIVIDER */}
        <span style={{ color: 'rgba(255, 255, 255, 0.2)', fontWeight: '300', fontSize: '11px', lineHeight: 1 }}>|</span>

        {/* RIGHT 50% PART: ON DUTY */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', lineHeight: 1 }}>
            <Zap size={12} color="#34D399" strokeWidth={2.5} style={{ display: 'block' }} />
            <span style={{ fontSize: '10px', fontWeight: '800', color: '#94A3B8', letterSpacing: '0.5px', lineHeight: 1 }}>
                ON DUTY:
            </span>
            <span style={{ fontSize: '12px', fontWeight: '900', color: '#FFFFFF', lineHeight: 1 }}>
                {workers.filter(w=>w.on_duty==1).length}
            </span>
        </div>
    </div>

    {/* 2. SHIFT SELECTION PILL BAR */}
    <div style={{ 
        background: '#1E293B', 
        padding: '6px 10px', 
        borderRadius: '24px', 
        display: 'flex', 
        alignItems: 'center', 
        gap: '10px',
        border: '1px solid rgba(255, 255, 255, 0.08)'
    }}>
        <span style={{ fontSize: '11px', fontWeight: '900', color: '#94A3B8', letterSpacing: '1px', textTransform: 'uppercase', paddingLeft: '8px' }}>
            SHIFT:
        </span>

        <div style={{ display: 'flex', flex: 1, alignItems: 'center', gap: '8px' }}>
            <button 
                onClick={(e) => { handleVoiceClick ? handleVoiceClick(e, " यह Day शिफ्ट Button है, इसमें आप Day की शिफ्ट वाले वर्कर्स की लिस्ट देख सकते हैं।", () => setActiveShiftView('Day'), 'tab-day') : setActiveShiftView('Day'); }} 
                style={{
                    flex: 1,
                    justifyContent: 'center',
                    fontSize: '11px', 
                    fontWeight: '900', 
                    background: activeShiftView === 'Day' ? 'rgba(56, 189, 248, 0.12)' : 'transparent', 
                    color: activeShiftView === 'Day' ? '#38BDF8' : '#94A3B8', 
                    padding: '8px 16px', 
                    borderRadius: '20px', 
                    border: activeShiftView === 'Day' ? '1px solid #38BDF8' : '1px solid transparent', 
                    whiteSpace: 'nowrap', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.25s ease',
                    boxShadow: activeShiftView === 'Day' ? '0 0 12px rgba(56, 189, 248, 0.25)' : 'none'
                }}
            >
                <Sun size={14} color={activeShiftView === 'Day' ? '#38BDF8' : '#94A3B8'} strokeWidth={2.5} /> 
                DAY SHIFT
            </button>

            <button 
                onClick={(e) => { handleVoiceClick ? handleVoiceClick(e, " यह Night शिफ्ट Button है, इसमें आप रात की शिफ्ट वाले वर्कर्स की लिस्ट देख सकते हैं।", () => setActiveShiftView('Night'), 'tab-night') : setActiveShiftView('Night'); }} 
                style={{
                    flex: 1,
                    justifyContent: 'center',
                    fontSize: '11px', 
                    fontWeight: '900', 
                    background: activeShiftView === 'Night' ? 'rgba(56, 189, 248, 0.12)' : 'transparent', 
                    color: activeShiftView === 'Night' ? '#38BDF8' : '#94A3B8', 
                    padding: '8px 16px', 
                    borderRadius: '20px', 
                    border: activeShiftView === 'Night' ? '1px solid #38BDF8' : '1px solid transparent', 
                    whiteSpace: 'nowrap', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.25s ease',
                    boxShadow: activeShiftView === 'Night' ? '0 0 12px rgba(56, 189, 248, 0.25)' : 'none'
                }}
            >
                <Moon size={14} color={activeShiftView === 'Night' ? '#38BDF8' : '#94A3B8'} strokeWidth={2.5} /> 
                NIGHT SHIFT
            </button>
        </div>
    </div>
</div>

                <div style={{display:'flex', flexDirection:'column', gap:'5px'}}>
                    {sortedWorkers.filter(w => w.shift === activeShiftView && w.is_badli == 0).map((worker, index) => (
                        <WorkerCard 
                            key={worker.id} 
                            index={index}
                            worker={worker} 
                            looms={looms}
                            currentTime={currentTime}
                            onMenuToggle={handleMenuToggle}
                            isMenuOpen={menuOpenId === worker.id}
                            onEditClick={handleEditProfileClick}
                            onOvertimeClick={handleOvertimeClick}
                            onEndBadliClick={handleEndBadliClick}
                            onViewHistory={handleViewHistory}
                            onToggleDuty={handleToggleCheck}
                            voiceAssist={voiceAssist}
                            handleVoiceClick={handleVoiceClick}
                            activeGuideFlow={activeGuideFlow}
                            onCancelBadgeClick={handleCancelBadgeInit}
                        />
                    ))}
                </div>

                {badliWorkers.length > 0 && (
                    <div style={{marginTop:'20px'}}>
                        <div style={{display:'flex', alignItems:'center', gap:'6px', marginBottom:'12px'}}>
                            <div style={{height:'1px', flex:1, background:'rgba(255,255,255,0.3)'}}></div>
                            <span style={{fontSize:'8px', fontWeight:'700', color:'#D97706', textTransform:'uppercase', letterSpacing:'1px'}}>Substitute / Badli</span>
                            <div style={{height:'1px', flex:1, background:'rgba(255,255,255,0.3)'}}></div>
                        </div>
                       <div style={{display:'flex', flexDirection:'column', gap:'5px'}}>
                            {badliWorkers.map(worker => (
                                <WorkerCard 
                                    key={worker.id} 
                                    worker={worker} 
                                    looms={looms}
                                    currentTime={currentTime}
                                    onMenuToggle={handleMenuToggle}
                                    isMenuOpen={menuOpenId === worker.id}
                                    onEditClick={handleEditProfileClick}
                                    onOvertimeClick={handleOvertimeClick}
                                    onEndBadliClick={handleEndBadliClick}
                                    onViewHistory={handleViewHistory}
                                    onToggleDuty={handleToggleCheck}
                                    voiceAssist={voiceAssist}
                                    handleVoiceClick={handleVoiceClick}
                                    onCancelBadgeClick={handleCancelBadgeInit} /* 🔥 YAHAN MISSING THA 🔥 */
                                    activeGuideFlow={activeGuideFlow}          /* 🔥 YAHAN MISSING THA 🔥 */
                                />
                            ))}
                        </div>
                    </div>
                )}

                {visibleWorkers.length === 0 && (
                    <div style={{textAlign:'center', padding:'32px', color:'#444', fontWeight:'500', fontSize:'11px'}}>
                        No {activeShiftView} workers found.
                    </div>
                )}
            </div>

          {/* 🌟 सही लेजेंड: यह फॉर्म (showModal) खुलने पर 100% गायब हो जाएगा 🌟 */}
{(!showModal && !showBadliModal && !showOvertimeModal && !showGuideModal) && (
    <div onClick={(e) => handleVoiceClick(e, " यह नीचे कलर्स का मतलब बताता है, हरी लाइट के साथ हरी लाइट बंद-चालू हो रही है, इसका मतलबब Worker Normal Duty पर है, हरी लाइट के साथ पीली लाइट बंद-चालू हो रही है, इसका मतलब Worker बदली Duty कर रहा है, हरी लाइट के साथ नीली लाइट बंद-चालू हो रही है, इसका मतलब Worker Overtime Duty पर है।", null)} style={{
        position: 'fixed', 
        /* 🌟 bottom को नेविगेशन के हिसाब से सेट किया गया है 🌟 */
        bottom: '-25px', 
        left: 0, 
        width: '100%', 
        background: '#ffffff', 
        borderTop: '1px solid #e2e8f0',
        padding: '10px 12px', 
        display: 'flex', 
        justifyContent: 'space-around', 
        alignItems: 'center', 
        /* 🌟 ट्यूटोरियल के वक्त zIndex 0 ताकि यह परछाई के पीछे रहे 🌟 */
        zIndex: activeGuideFlow ? 0 : 9999, 
        boxShadow: '0 -4px 10px rgba(0,0,0,0.03)'
    }}>
        <div style={{display:'flex', alignItems:'center', gap:'5px'}}>
            <div style={{width:'6px', height:'6px', borderRadius:'50%', background:'#10B981'}}></div>
            <div style={{width:'6px', height:'6px', borderRadius:'50%', background:'#10B981'}}></div>
            <span style={{fontSize:'8px', fontWeight:'600', color:'#16A34A'}}>DUTY</span>
        </div>
        <div style={{display:'flex', alignItems:'center', gap:'3px'}}>
            <div style={{display:'flex', gap:'2px'}}>
                <div style={{width:'6px', height:'6px', borderRadius:'50%', background:'#10B981'}}></div>
                <div style={{width:'6px', height:'6px', borderRadius:'50%', background:'#EAB308'}}></div>
            </div>
            <span style={{fontSize:'8px', fontWeight:'600', color:'#D97706'}}>BADLI</span>
        </div>
        <div style={{display:'flex', alignItems:'center', gap:'3px'}}>
            <div style={{display:'flex', gap:'2px'}}>
                <div style={{width:'6px', height:'6px', borderRadius:'50%', background:'#10B981'}}></div>
                <div style={{width:'6px', height:'6px', borderRadius:'50%', background:'#3B82F6'}}></div>
            </div>
            <span style={{fontSize:'8px', fontWeight:'600', color:'#2563EB'}}>OVERTIME(OT)</span>
        </div>
        <div style={{display:'flex', alignItems:'center', gap:'5px'}}>
            <div style={{width:'6px', height:'6px', borderRadius:'50%', background:'rgba(0,0,0,0.1)'}}></div>
            <span style={{fontSize:'8px', fontWeight:'600', color:'#111'}}>OFF</span>
        </div>
    </div>
)}

         {/* CONFLICT CONFIRMATION MODAL */}
            <ConfirmModal 
                isOpen={Boolean(switchConfirm)}
                onClose={() => setSwitchConfirm(null)}
                onConfirm={confirmSwitch}
                title="Loom Conflict"
                message={switchConfirm ? `Looms are currently active under ${switchConfirm.active.name}. Switch duty to ${switchConfirm.new.name}?` : ""}
                confirmText="Confirm Transfer"
                isDanger={false}
            />

          {/* 🌟 NAYA: CANCEL PENDING BADGE CONFIRMATION MODAL 🌟 */}
            <ConfirmModal 
                isOpen={Boolean(cancelBadgeConfirm)}
                onClose={() => setCancelBadgeConfirm(null)}
                onConfirm={processCancelBadge}
                title="Cancel Pending Change"
                // NAYA: Message ko English me change kar diya gaya hai
                message={cancelBadgeConfirm ? `Are you sure you want to cancel the pending change for "${cancelBadgeConfirm.label}"?` : ""}
                confirmText="Yes, Cancel"
                isDanger={true}
                loading={isCancelling} 
            />

            {showBadliEndModal && (
                <BadliEndModal 
                    worker={showBadliEndModal} 
                    looms={looms} 
                    onClose={() => setShowBadliEndModal(null)} 
                    onConfirm={processEndBadli} 
                    handleVoiceClick={handleVoiceClick}
                />
            )}

            {showOvertimeModal && (
                <OvertimeModal 
                    worker={showOvertimeModal} 
                    looms={looms} 
                    onClose={() => setShowOvertimeModal(null)} 
                    onSave={refreshData} 
                    handleVoiceClick={handleVoiceClick} 
                />
            )}
            
            {showBadliModal && (
                <BadliModal 
                    workers={workers} 
                    onClose={() => setShowBadliModal(false)} 
                    onSave={refreshData} 
                    handleVoiceClick={handleVoiceClick} 
                    handleFocusVoice={handleFocusVoice} 
                />
            )}
            
          {/* ADD / EDIT WORKER MODAL */}
            <EditWorkerModal 
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                workerData={editingWorker}
                onSave={async () => {
                    await refreshData();
                    setShowModal(false);
                }}
                availableLooms={looms}
                handleVoiceClick={handleVoiceClick}
                handleFocusVoice={handleFocusVoice}
                handleTextBlurVoice={handleTextBlurVoice}
            />

            {/* 👇 Yahan SmartGuideModal add kiya gaya hai 👇 */}
            <SmartGuideModal 
                isOpen={showGuideModal} 
                onClose={() => setShowGuideModal(false)} 
                handleVoiceClick={handleVoiceClick} 
                startSmartGuide={startSmartGuide} 
            />

        
        </div>
    );
};

const styles = {
    container: { 
        minHeight: '100vh', 
        background: 'transparent', 
        paddingBottom: '120px', 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        width: '100%', 
        height: '100%', 
        overflowY: 'auto', 
        overflowX: 'hidden', 
        zIndex: 100,
        WebkitOverflowScrolling: 'touch',
        overscrollBehaviorY: 'contain'
    },
    header: { background: 'rgba(255, 255, 255, 0.25)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', padding: '12px 16px', borderBottom: '1px solid rgba(255, 255, 255, 0.2)', display: 'flex', alignItems: 'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:1000, transform: 'translateZ(0)' }, 
    backBtn: { background: 'rgba(255,255,255,0.4)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.3)', cursor: 'pointer', padding: '4px', display:'flex', alignItems:'center' },
    content: { padding: '16px',paddingBottom: '70px', maxWidth: '600px', margin: '0 auto' },
    premiumButtonStyle: { width: '100%', background: '#1A1A1A', color: 'white', border: 'none', borderRadius: '12px', padding: '12px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', boxShadow: '0 6px 16px rgba(0,0,0,0.2)' },
    iconBtnSquare: { width:'28px', height:'28px', borderRadius:'8px', border:'1px solid rgba(255,255,255,0.4)', background:'rgba(255,255,255,0.3)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' },
   /* 🌟 FIX: मेनू आइटम और टेक्स्ट का साइज़ 10px किया गया और पैडिंग कॉम्पैक्ट की गई */
    menuItem: { display:'flex',fontSize: '10px', alignItems:'center', gap:'8px', width:'100%', padding:'6px 10px', marginBottom:'4px', border:'1px solid rgba(255,255,255,0.4)', borderRadius:'10px', background:'rgba(255,255,255,0.5)', cursor:'pointer', textAlign:'left', transition: 'all 0.2s ease' },
   menuText: { fontSize: '10px', fontWeight: '600', color: '#111' },
   
    // Scrollable Select
    scrollBox: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' },
    scrollSelect: { 
        width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.5)',
        fontSize: '16px', fontWeight: '600', color: '#111', background: 'rgba(255,255,255,0.6)', outline: 'none',
        appearance: 'none', textAlign: 'center', cursor: 'pointer'
    },
    timeLabelBottom: { fontSize: '8px', fontWeight: '600', color: '#444', marginTop: '4px' }
};

export default Workers;